import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { connect } from 'react-redux'
import * as tf from '@tensorflow/tfjs'
import ProgramHeader from '../ReusableComponents/Header/ProgramHeader'
import DialogModal from '../ReusableComponents/DialogModal/DialogModal'
import renderImage from '../../source/importImg'
import renderPrgImage from '../../source/programImg'
import { MOBILENET_URL } from '../AppMode/teachableMachine/TrainMachine/constants/modelConstants'
import useAudioModel from '../AppMode/teachableMachine/audio/useAudioModel'
import FrameSampler from './vision/frameSampler'
import { detectColourRGB, distanceToIntensity } from './vision/colourDetector'
import MotionDetector from './vision/motionDetector'
import {
    getGestureRecognizer,
    nextVideoTimestamp,
    handVectorFromResult,
    getPoseLandmarker,
    poseVectorFromResult,
} from './vision/mediapipeRuntime'
import { getDetector } from './vision/detectionProvider'
import { localizeObjects } from './vision/objectLocalizer'
import { segmentBlobs } from './vision/blobSegmenter'
import { countParts } from './vision/detectors/partCounter'
import ObjectTracker from './vision/objectTracker'
import {
    loadPaths,
    savePaths,
    makePathProgress,
    stepPathProgress,
} from './vision/pathUtils'
import { drawPaths } from './vision/pathRenderer'
import { createFaceTracker } from './vision/faceTracker'
import {
    acquireCameraStream,
    stopCameraStream,
    listCameraOptions,
    sanitizeCameraId,
    MOBILE_CAMERA_ID,
} from './vision/cameraConfig'
import {
    mobileConnected,
    mobilePairingRoom,
    startMobilePairing,
    stopMobilePairing,
    setPairingStatusCallback,
} from './webrtc/mobileLink'
import { makeRoomCode } from './webrtc/signalingConfig'
import QRCode from 'qrcode'
import { loadOutputs } from './outputInstances'
import { getMediaURL } from './mediaStore'
import {
    loadEnabledVars,
    applyVarOp,
    resolveActionText,
} from './aiVariables'
import ToggleSwitch from '../ReusableComponents/ToggleSwitch/ToggleSwitch'
import { Expand, Shrink, Volume2, Info, X } from 'lucide-react'
import {
    compName_character,
    livePortsNumber,
    klawLiveServoPortsNumber,
} from '../logic/panels/helpers/LiveButton'
import { servoById } from './deviceCapabilities'
import {
    recognizeText,
    preprocessCanvasForOcr,
    OCR_MIN_CONFIDENCE,
} from './vision/ocrRuntime'
import { ChromeNote } from './PhonePairNote'
import { detectMarkerIds } from './vision/arucoRuntime'
import { stopByteCondition } from '../../utils/utils'
import styles from './AiExecute.module.css'

// Map an accessory's stored slider value onto the byte the firmware expects,
// mirroring the proven hardware live-write (logic SliderRow.sendLiveChange):
// 270/sg90 servos scale to 0-180; the 360 continuous servo offsets to 0-200;
// motors send magnitude only (single-port live output has no direction).
const liveWriteValue = (accessory, rawValue) => {
    let value = Number(rawValue) || 0
    if (accessory === 'servo_motor_270') {
        value = Math.round(0.6667 * value)
    } else if (accessory === 'servo_motor_360') {
        value = 100 + value
    } else if (
        [
            'dc_motor',
            'geared_motor',
            'mini_geared_motor',
            'dynamex_motor',
        ].includes(accessory)
    ) {
        value = Math.abs(value)
        if (accessory === 'dynamex_motor') value = Math.round(value * 0.8)
    }
    return value & 0xff
}

// Motor accessories drive a full H-bridge: the two pins of a socket set
// direction. Forward puts the speed on the forward pin (reverse pin 0); backward
// swaps them; 0 stops both.
const MOTOR_ACCS = [
    'dc_motor',
    'geared_motor',
    'mini_geared_motor',
    'dynamex_motor',
]
// A dedicated motor driver spans two firmware pins, like a group socket. The UI
// labels the drivers M1 (firmware pins M1/M2) and M2 (firmware pins M3/M4); some
// boards label the second driver M3 — both map to M3/M4.
const MOTOR_PIN_PAIRS = {
    M1: ['M1', 'M2'],
    M2: ['M3', 'M4'],
    M3: ['M3', 'M4'],
}
// The two firmware pin numbers for a motor's socket: [forwardPin, reversePin].
const motorPinPorts = (device, port) => {
    const pm = livePortsNumber?.[device] || livePortsNumber.Ace
    let names
    if (MOTOR_PIN_PAIRS[port]) names = MOTOR_PIN_PAIRS[port]
    else {
        const g = /^([A-Z])/.exec(port)
        names = g ? [`${g[1]}1`, `${g[1]}2`] : [port]
    }
    return names
        .map((n) => pm?.[n] ?? livePortsNumber.Ace?.[n])
        .filter((x) => x != null)
}
// Motor speed magnitude for the driven pin (direction is chosen by pin, not
// sign). Dynamex tops out lower, matching the proven live-write.
const motorSpeed = (accessory, rawValue) => {
    let v = Math.abs(Number(rawValue) || 0)
    if (accessory === 'dynamex_motor') v = Math.round(v * 0.8)
    return v & 0xff
}

const clampPct = (v) => Math.max(0, Math.min(100, v))

// Normalised keyboard key name used by both the capture listeners and the
// condition panel's key dropdown (lowercase; space spelled out).
const normalizeKey = (k) =>
    k === ' ' || k === 'Spacebar' ? 'space' : String(k).toLowerCase()

// Mouse button index -> stable name matching the panel's options.
const mouseButtonName = (b) =>
    b === 0 ? 'left' : b === 2 ? 'right' : b === 1 ? 'middle' : String(b)

// KNN helpers: L2-normalize a vector and cosine similarity (dot of unit vecs).
const l2normalize = (vec) => {
    let sum = 0
    for (const x of vec) sum += x * x
    const norm = Math.sqrt(sum) || 1
    return vec.map((x) => x / norm)
}
const dot = (a, b) => {
    let s = 0
    const n = Math.min(a.length, b.length)
    for (let i = 0; i < n; i++) s += a[i] * b[i]
    return s
}

const readJSON = (key, fallback) => {
    try {
        return JSON.parse(sessionStorage.getItem(key)) || fallback
    } catch {
        return fallback
    }
}

// Which trained TYPE a recognition source needs. A model can hold a separately
// trained head per type, keyed `${modelId}__${type}`.
const sourceToType = (src) =>
    src === 'RECOGNIZE_OBJECT' || src === 'PART_COUNT'
        ? 'object'
        : src === 'RECOGNIZE_POSE'
            ? 'pose'
            : src === 'RECOGNIZE_HAND'
                ? 'gesture'
                : 'face'

const modelKeyFor = (cfg) =>
    cfg.modelId ? `${cfg.modelId}__${sourceToType(cfg.source)}` : null

// The recognition result for a rule's chosen model+type, falling back to the
// default result when a node has no explicit model.
const recognitionFor = (cfg, p) => {
    const key = modelKeyFor(cfg)
    return (key && p.recognitions && p.recognitions[key]) || p.recognition
}

// True when a face at (fx,fy) percent falls inside the selected ROI region.
// selectedRegionId 'none'/'all'/undefined means no region gating.
const faceInRegion = (cfg, p) => {
    const id = cfg.selectedRegionId
    if (!id || id === 'none' || id === 'all') return true
    const regions =
        (cfg.modelId && p.regionsByModel && p.regionsByModel[String(cfg.modelId)]) ||
        p.regions ||
        []
    const region = regions.find((r) => String(r.id) === String(id))
    if (!region) return true
    const fx = p.face.x / 100
    const fy = p.face.y / 100
    return (
        fx >= region.xPercent &&
        fx <= region.xPercent + region.widthPercent &&
        fy >= region.yPercent &&
        fy <= region.yPercent + region.heightPercent
    )
}

// True when a recognized object's box centre falls inside the selected ROI
// region — so "if object in region" only fires while it's actually there. No
// region selected (none/all) means no gating; no box yet means don't block.
const recognitionInRegion = (cfg, p, rec) => {
    const id = cfg.selectedRegionId
    if (!id || id === 'none' || id === 'all') return true
    const regions =
        (cfg.modelId &&
            p.regionsByModel &&
            p.regionsByModel[String(cfg.modelId)]) ||
        p.regions ||
        []
    const region = regions.find((r) => String(r.id) === String(id))
    if (!region || !rec || !rec.box) return true
    const cx = rec.box.x + rec.box.w / 2
    const cy = rec.box.y + rec.box.h / 2
    return (
        cx >= region.xPercent &&
        cx <= region.xPercent + region.widthPercent &&
        cy >= region.yPercent &&
        cy <= region.yPercent + region.heightPercent
    )
}

// True when segments AB and CD intersect. Used for line-crossing detection.
const segmentsIntersect = (a, b, c, d) => {
    const ccw = (p1, p2, p3) =>
        (p3.y - p1.y) * (p2.x - p1.x) - (p2.y - p1.y) * (p3.x - p1.x)
    const d1 = ccw(c, d, a)
    const d2 = ccw(c, d, b)
    const d3 = ccw(a, b, c)
    const d4 = ccw(a, b, d)
    return (
        ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
        ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
    )
}

// Resolve one condition source to a numeric value against this tick's
// perception (trained recognition, face tracking, colour, motion, speech, usb).
const sourceValue = (cfg, p) => {
    const src = cfg.source
    if (src === 'PATH_CROSS') {
        // Fire a short pulse when a matching target completes the chosen path in
        // the chosen direction. p.pathEvents = recent { pathId, dir, class,
        // trainedKey, at } produced by the tracker in runDetection.
        const events = p.pathEvents || []
        const now = Date.now()
        for (const ev of events) {
            if (now - ev.at > 700) continue
            // Only filter by path when one is pinned; an unset/stale pathId
            // matches any path so a mis-saved selection still fires.
            if (
                cfg.pathId &&
                p.pathIds &&
                p.pathIds.includes(String(cfg.pathId)) &&
                String(ev.pathId) !== String(cfg.pathId)
            )
                continue
            // 'forward' = travelled start -> end, 'backward' = end -> start.
            // 'any' matches either completed route.
            if (cfg.pathDir && cfg.pathDir !== 'any' && ev.dir !== cfg.pathDir)
                continue
            // Target match: a COCO class, 'any', or a trained identity.
            const tgt = cfg.pathTarget || 'any'
            if (tgt === 'any') return 1
            if (tgt.startsWith('trained:')) {
                if (ev.trainedKey === tgt) return 1
                continue
            }
            if (ev.class === tgt) return 1
        }
        return 0
    }
    if (src === 'SPEECH_WORD') {
        const w = (cfg.speechWord || '').trim().toLowerCase()
        if (!w || !p.spokenWord) return 0
        return Date.now() - p.spokenWord.at < 1500 &&
            p.spokenWord.text.includes(w)
            ? 1
            : 0
    }
    if (src === 'OCR_TEXT') {
        // Fires while the target text is seen on the camera (recent OCR result).
        const w = (cfg.ocrText || '').trim().toLowerCase()
        if (!w || !p.ocrText) return 0
        return Date.now() - p.ocrText.at < 3000 &&
            p.ocrText.text.toLowerCase().includes(w)
            ? 1
            : 0
    }
    if (src === 'RECOGNIZE_FACE') {
        // A trained model scopes to a specific class; without one, any face
        // presence counts (matches the original fallback behaviour). A drawn
        // region further requires the face to be inside it.
        const rec = recognitionFor(cfg, p)
        let ok
        if (rec) {
            const recognized = rec.conf > 0.7
            const classOk =
                !cfg.selectedClassId ||
                cfg.selectedClassId === 'none' ||
                String(rec.classId) === String(cfg.selectedClassId)
            ok = recognized && classOk
        } else {
            ok = !!p.face.present
        }
        if (ok && !faceInRegion(cfg, p)) ok = false
        return ok ? 1 : 0
    }
    if (
        src === 'RECOGNIZE_OBJECT' ||
        src === 'RECOGNIZE_POSE' ||
        src === 'RECOGNIZE_HAND'
    ) {
        // Object, Pose and trained Gesture all use a trained model + class match.
        const rec = recognitionFor(cfg, p)
        const recognized = rec && rec.conf > 0.7 ? 1 : 0
        const classOk =
            !cfg.selectedClassId ||
            cfg.selectedClassId === 'none' ||
            (rec && String(rec.classId) === String(cfg.selectedClassId))
        // Objects are localized (have a box), so a chosen ROI region gates them;
        // pose/gesture have no box, so region gating doesn't apply.
        const regionOk =
            src === 'RECOGNIZE_OBJECT' ? recognitionInRegion(cfg, p, rec) : true
        return recognized && classOk && regionOk ? 1 : 0
    }
    if (src === 'RECOGNIZE_GESTURE') {
        // Built-in MediaPipe hand gesture (not the trained classifier).
        if (!p.gesture) return 0
        return p.gesture.name === cfg.gestureName && p.gesture.conf > 0.5
            ? 1
            : 0
    }
    if (src === 'DETECT_MOTION') {
        // Boolean: is there motion right now (debounced in runDetection).
        return p.motion.moving ? 1 : 0
    }
    if (src === 'DETECT_COLOR') {
        if (!p.frame || !cfg.colorTarget) return 0
        const res = detectColourRGB(
            p.frame.image,
            p.frame.w,
            p.frame.h,
            cfg.colorTarget,
            null
        )
        return res.matched ? 1 : 0
    }
    if (src === 'SPEECH') {
        return p.speech ? p.speech(cfg) : 0
    }
    if (src === 'KEY_PRESS') {
        const keys = p.keys
        if (!keys) return 0
        if (!cfg.keyName || cfg.keyName === 'any') return keys.size > 0 ? 1 : 0
        return keys.has(cfg.keyName) ? 1 : 0
    }
    if (src === 'MOUSE_CLICK') {
        const btns = p.mouse
        if (!btns) return 0
        if (!cfg.mouseButton || cfg.mouseButton === 'any')
            return btns.size > 0 ? 1 : 0
        return btns.has(cfg.mouseButton) ? 1 : 0
    }
    if (src === 'SCREEN_BUTTON') {
        const b = p.buttons || {}
        return b[cfg.buttonId || 'button1'] ? 1 : 0
    }
    if (src === 'SCREEN_TOGGLE') {
        const t = p.toggles || {}
        return t[cfg.toggleId] ? 1 : 0
    }
    if (src === 'PART_COUNT') {
        return Number(p.partCount || 0)
    }
    // Counter/flag comparison: source is "VAR:<id>".
    if (typeof src === 'string' && src.startsWith('VAR:')) {
        const vars = p.vars || {}
        return Number(vars[src.slice(4)] || 0)
    }
    if (src === 'USB') {
        return Number(p.usb ?? 0)
    }
    return 0
}

const compareOp = (operator, v, a, b) => {
    switch (operator) {
        case 'greaterThan':
            return v > a
        case 'lessThan':
            return v < a
        case 'inBetween':
            return v >= Math.min(a, b) && v <= Math.max(a, b)
        case 'notEqualTo':
            return v !== a
        case 'equalTo':
        default:
            return v === a
    }
}

// Friendly label for a path-completion direction in the stats log.
const pathDirLabel = (d) =>
    d === 'forward' ? 'start → end' : d === 'backward' ? 'end → start' : d

// Safe default perception so a decision evaluated before the first camera frame
// (or with a partial perception) never throws on a missing field.
const EMPTY_PERCEPTION = {
    face: { present: 0, x: 0, y: 0, size: 0, speed: 0 },
    objects: [],
    motion: { moving: false, level: 0 },
    frame: null,
    keys: new Set(),
    mouse: new Set(),
    buttons: {},
    toggles: {},
    pathEvents: [],
    pathIds: [],
    recognition: null,
    recognitions: {},
    gesture: null,
    spokenWord: null,
    partCount: 0,
    vars: {},
    ocrText: null,
    markers: null,
    speechTranscript: '',
}

// Evaluate one decision node's stored condition against this tick's perception.
const evalCondition = (cfg, p) => {
    if (!cfg || !cfg.source) return false
    const pp = { ...EMPTY_PERCEPTION, ...(p || {}) }
    try {
        if (cfg.source === 'MARKER_ID') {
            // Fires if any freshly-detected marker id satisfies the comparison.
            const m = pp.markers
            if (!m || Date.now() - m.at > 700 || !m.ids || !m.ids.length)
                return false
            const a = Number(cfg.value ?? 0)
            const b = Number(cfg.value2 ?? 0)
            return m.ids.some((mid) => compareOp(cfg.operator, mid, a, b))
        }
        const v = Number(sourceValue(cfg, pp))
        // A data-variable rule can compare against another variable's live value
        // instead of a typed number (the Value/Variable toggle in the panel).
        let a = Number(cfg.value ?? 0)
        if (cfg.compareMode === 'variable' && cfg.compareVar) {
            a = Number((pp.vars || {})[cfg.compareVar] || 0)
        }
        return compareOp(cfg.operator, v, a, Number(cfg.value2 ?? 0))
    } catch (e) {
        return false
    }
}

// TensorFlow.js' own tf.io.listModels()/loadLayersModel throw an UNCATCHABLE
// error (raised inside their IndexedDB success handler, not the awaited
// promise) when the 'tensorflowjs' database exists but its object stores are
// missing — a corrupted/partial state we've seen in the field. Guard by first
// confirming the database exists and actually has both stores, without ever
// creating or upgrading it. Only then is it safe to call tf.io.listModels().
const TFJS_DB = 'tensorflowjs'
const TFJS_STORES = ['models_store', 'model_info_store']
const safeListModels = () =>
    new Promise((resolve) => {
        let settled = false
        const done = (v) => {
            if (!settled) {
                settled = true
                resolve(v)
            }
        }
        const idb = typeof window !== 'undefined' && window.indexedDB
        if (!idb) return done({})
        // Inspect the existing DB's stores (open with no version so no upgrade
        // is triggered), then decide whether tf.io.listModels() is safe.
        const inspect = () => {
            let req
            try {
                req = idb.open(TFJS_DB)
            } catch (e) {
                return done({})
            }
            let created = false
            req.onupgradeneeded = () => {
                // The DB didn't exist and is being created empty — abort so we
                // don't leave a storeless DB that would crash tf later.
                created = true
                try {
                    req.transaction.abort()
                } catch (e) {
                    /* ignore */
                }
            }
            req.onerror = () => done({})
            req.onsuccess = () => {
                const db = req.result
                let ok = false
                try {
                    ok =
                        !created &&
                        TFJS_STORES.every((s) =>
                            db.objectStoreNames.contains(s)
                        )
                } catch (e) {
                    ok = false
                }
                try {
                    db.close()
                } catch (e) {
                    /* ignore */
                }
                if (!ok) return done({})
                tf.io
                    .listModels()
                    .then(done)
                    .catch(() => done({}))
            }
        }
        // Prefer databases() (Chrome) so a fresh user with no DB never opens
        // (and thus never creates) the 'tensorflowjs' database at all.
        if (typeof idb.databases === 'function') {
            idb.databases()
                .then((list) => {
                    const exists = (list || []).some(
                        (d) => d && d.name === TFJS_DB
                    )
                    if (!exists) return done({})
                    inspect()
                })
                .catch(() => inspect())
        } else {
            inspect()
        }
    })

const AiExecute = (props) => {
    const history = useHistory()
    const videoRef = useRef(null)
    const streamRef = useRef(null)
    const loopRef = useRef(null)
    const runningRef = useRef(false)
    const [running, setRunning] = useState(false)
    // Content currently shown per output instance: { [outputId]: {kind,text|src} }
    const [firedOutputs, setFiredOutputs] = useState({})
    // Detection boxes are drawn on the overlay canvas (via the contain
    // transform) so they stay aligned with the letterboxed video; refs feed
    // the draw loop (no per-frame React re-render needed).
    const faceBoxRef = useRef(null)
    const objectBoxesRef = useRef([])
    const [showDeviceDialog, setShowDeviceDialog] = useState(false)
    const [modelLoading, setModelLoading] = useState(false)
    // Camera device chooser (switch webcams during Execute). Defaults to the
    // camera chosen in Configure > Inputs so Execute uses the same device.
    const configuredCameraId = sessionStorage.getItem('aiCameraDeviceId') || ''
    const [cameras, setCameras] = useState([])
    const [activeCameraId, setActiveCameraId] = useState(configuredCameraId)
    const [camMenuOpen, setCamMenuOpen] = useState(false)
    const activeCameraIdRef = useRef(configuredCameraId)
    // Phone-camera reconnect helper: the QR/steps to re-pair a phone that
    // dropped mid-session (the room stays valid, so re-scanning reconnects
    // without reloading the page).
    const [showPhoneHelp, setShowPhoneHelp] = useState(false)
    const [phoneRoom, setPhoneRoom] = useState('')
    const [phoneQr, setPhoneQr] = useState('')
    // 'connected' | 'dropped' | null. Driven by the WebRTC connection-state
    // callback (no polling) so we can show a "disconnected" banner on drop.
    const [phoneStatus, setPhoneStatus] = useState(null)
    const showPhoneHelpRef = useRef(false)
    useEffect(() => {
        showPhoneHelpRef.current = showPhoneHelp
    }, [showPhoneHelp])
    // Single status handler for the paired phone: update the banner state, and
    // if the reconnect popup is open when it reconnects, close it and switch to
    // the phone feed.
    const handlePhoneState = (s) => {
        if (s === 'connected') {
            setPhoneStatus('connected')
            // Resume the phone feed when phone is still the chosen source (the
            // reconnect panel is open, or the phone is the active/intended
            // camera). If the user switched to a computer camera, leave it be.
            if (
                showPhoneHelpRef.current ||
                activeCameraIdRef.current === MOBILE_CAMERA_ID
            ) {
                setShowPhoneHelp(false)
                setPhoneQr('')
                // On RECONNECT the phone sends a brand-new stream, but the
                // deviceId is still MOBILE_CAMERA_ID — so selectCamera would
                // no-op on its "same device" guard and the <video> would keep
                // the old, dead track (blank/frozen). Re-attach the fresh stream
                // directly via openCamera, which has no such guard.
                activeCameraIdRef.current = MOBILE_CAMERA_ID
                faceTrackPrevRef.current = null
                if (motionRef.current) motionRef.current.reset()
                openCamera(MOBILE_CAMERA_ID)
            }
        } else if (
            s === 'disconnected' ||
            s === 'failed' ||
            s === 'closed' ||
            s === 'error'
        ) {
            setPhoneStatus('dropped')
        }
    }
    // Listen for the paired phone's connection state (the module-scope link
    // survives navigation from Configure, so this catches mid-session drops).
    useEffect(() => {
        setPairingStatusCallback(handlePhoneState)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    // Open the options panel — no camera change, no QR yet (progressive: the
    // steps come first; the user reveals the QR only if the session page is
    // gone). Refresh the camera list so the "another camera" options are current.
    const openPhoneHelp = () => {
        refreshCameras()
        setShowPhoneHelp(true)
    }
    const closePhoneHelp = () => {
        setShowPhoneHelp(false)
        setPhoneQr('') // reset so it starts minimal next time
    }
    // Reveal the QR on demand: ensure a live room (reuse the session's, else
    // start a fresh pairing) and render its code.
    const revealPhoneQr = async () => {
        let room = mobilePairingRoom()
        if (!room) {
            room = makeRoomCode()
            startMobilePairing(room, handlePhoneState)
        } else {
            setPairingStatusCallback(handlePhoneState)
        }
        setPhoneRoom(room)
        try {
            const url = `${window.location.origin}/mobilecamera?room=${room}`
            setPhoneQr(await QRCode.toDataURL(url, { width: 190, margin: 1 }))
        } catch (e) {
            /* the code text is still shown */
        }
    }
    // Hand off to the existing camera dropdown to pick another camera (avoids
    // duplicating the camera list inside this panel).
    const pickAnotherCamera = () => {
        setShowPhoneHelp(false)
        setPhoneQr('')
        refreshCameras()
        setCamMenuOpen(true)
    }
    // No camera to use — stop the run (AI can't run without a camera).
    const stopFromPhoneHelp = () => {
        setShowPhoneHelp(false)
        setPhoneStatus(null)
        if (runningRef.current) stop()
    }
    // Force a BRAND-NEW code + QR. The reliable escape when the phone reports
    // "code already in use": after the phone changed Wi-Fi/network, the old
    // session lingers on the server and blocks the old code, so start clean.
    const freshPhoneQr = async () => {
        stopMobilePairing()
        const room = makeRoomCode()
        startMobilePairing(room, handlePhoneState)
        setPairingStatusCallback(handlePhoneState)
        setPhoneStatus(null)
        setPhoneRoom(room)
        try {
            const url = `${window.location.origin}/mobilecamera?room=${room}`
            setPhoneQr(await QRCode.toDataURL(url, { width: 190, margin: 1 }))
        } catch (e) {
            /* the code text is still shown */
        }
    }
    // End the phone-camera session entirely so a fresh pairing can start clean.
    const disconnectPhone = () => {
        stopMobilePairing()
        setPhoneStatus(null)
        setPhoneQr('')
        setPhoneRoom('')
    }

    const enable = readJSON('enable', {})
    const layout = readJSON('aiCodeLayout', {})
    const selectedFeatures = readJSON('aiCodeSelectedFeatures', [])
    // Object tracking (crossing line + trajectory) only applies when the
    // Tracking feature is selected on the Select Feature screen. When it's
    // deselected, a previously drawn line/trajectory must not linger in Execute.
    const trackingSelected = selectedFeatures.includes('ai/tracking')
    const cameraEnabled = enable?.s1?.cam === true
    // Camera removed from the layout: keep it streaming (detection still runs)
    // but hide the panel so an output (e.g. a full-screen image) can take over.
    const cameraHidden = layout?.cameraRemoved === true
    // Output instances placed in Configure > Output.
    const outputInstances = loadOutputs()
    const fps = parseInt(sessionStorage.getItem('aiCameraFps')) || 16

    const posStyle = (p, fallback) => {
        const r = p || fallback
        return {
            position: 'absolute',
            left: `${r.x}%`,
            top: `${r.y}%`,
            width: `${r.w}%`,
            height: `${r.h}%`,
        }
    }

    const headerData = {
        selected: 3,
        options: [
            <p key="select-feature">Select Feature</p>,
            <p key="configure">Configure</p>,
            <p key="code">Code</p>,
            <p key="execute">Execute</p>,
        ],
    }

    // Drive a robot servo (Humanoid/Hexapod smart servo, or Klaw PWM servo).
    // Smart servo -> [83,77,id,MSB,LSB] (16-bit position). Klaw -> the LW servo
    // packet [76,87,83,klawPort,round(0.6*value)]. Protocol/limits come from the
    // editable device capability map.
    const sendServo = (servoId, rawValue, { force = false } = {}) => {
        if (!props.worker) return
        const connectedDevice =
            sessionStorage.getItem('connectedDevice') || 'Ace'
        const servo = servoById(connectedDevice, servoId)
        if (!servo) return
        const key = `servo:${servoId}`
        const v = Math.round(Number(rawValue) || 0)
        if (!force && lastLiveRef.current[key] === v) return
        lastLiveRef.current[key] = v
        if (servo.proto === 'klaw') {
            const klawPort = klawLiveServoPortsNumber[servo.pdKey]
            if (klawPort == null) return
            props.worker.postMessage({
                type: 'writeArray',
                value: [76, 87, 83, klawPort, Math.round(0.6 * v) & 0xff],
            })
        } else {
            const val = Math.max(0, Math.min(65535, v))
            const msb = (val >> 8) & 0xff
            const lsb = val & 0xff
            props.worker.postMessage({
                type: 'writeArray',
                value: [83, 77, Number(servoId) & 0xff, msb, lsb],
            })
        }
    }

    // Trained teachable-machine model inference. Extractors (MobileNet for
    // image models, MoveNet for pose models) are shared; each rule's own trained
    // head is loaded per-model into modelsRef so different nodes can use
    // different models. Falls back to the browser FaceDetector (presence only)
    // when no trained model is available.
    const perceptionRef = useRef({})
    // Last byte value written per port, so live output is sent only when it
    // changes instead of every tick (protects the board's serial RX pipeline).
    const lastLiveRef = useRef({})
    // Desired live-write state for every port-based accessory (led/pwm/servo/
    // motor), keyed by firmware port number -> { char, value }. Flushed as ONE
    // batched 'L','A' … 'E','R' packet so every port in a block (and across
    // blocks) takes effect together, instead of a separate 'L','W' packet per
    // port where only the first survived (bug 52).
    const liveStateRef = useRef({})
    const mobilenetRef = useRef(null)
    const poseModelRef = useRef(null)
    // modelId -> { type:'image'|'pose', head, classIds, classNames }
    const modelsRef = useRef({})
    const gestureRecognizerRef = useRef(null)
    const cocoModelRef = useRef(null)
    const objTrackerRef = useRef(null)
    // Throttled custom-object localization (sliding-window is heavier than a
    // single classification, so run it a few times/sec and hold between).
    const objLocalizeAtRef = useRef(0)
    const customObjectsRef = useRef([])
    const objPrevRef = useRef({})
    // Live count of a trained part (blob segmentation + classifier). partCountRef
    // feeds perception/overlay each tick; the ref-mirrored value is surfaced.
    const partCountRef = useRef(0)
    const partBoxesRef = useRef([])
    const partCountAtRef = useRef(0)
    const countPartsEnabledRef = useRef(false)
    // Colour rules (spec 18.2): per-node consecutive-match streak so a rule only
    // fires after the colour has held for the configured number of frames, and a
    // live readout (mean RGB, distance, coverage) for the on-screen badge.
    const colorStreakRef = useRef({})
    // Set when any colour rule is present. colorRulesRef holds each rule's target
    // colour; colorReadoutsRef holds this tick's live intensity (0-100% closeness)
    // for each of those targets, shown in the Execute stats.
    const colorEnabledRef = useRef(false)
    const colorRulesRef = useRef([])
    const colorReadoutsRef = useRef([])
    // OCR (Tesseract): live text read off the camera, throttled since it is slow.
    const ocrEnabledRef = useRef(false)
    const ocrTextRef = useRef(null)
    const ocrAtRef = useRef(0)
    const ocrBusyRef = useRef(false)
    const ocrCanvasRef = useRef(null)
    // ArUco markers: { ids:[...], at } from the latest detected frame.
    const markerEnabledRef = useRef(false)
    const markersRef = useRef(null)
    const markerAtRef = useRef(0)
    const markerBusyRef = useRef(false)
    // Which trained object/class the count targets (from the PART_COUNT rule).
    // When a class is chosen, blobs are classified and only matches are counted;
    // otherwise every segmented blob is counted.
    const countTargetRef = useRef(null)
    // Higher-res frame (than the 256x192 analysis sampler) for segmenting many
    // small parts; normalized blob boxes map back regardless of resolution.
    const countCanvasRef = useRef(null)
    const grabCountFrame = (video) => {
        const vw = video.videoWidth
        const vh = video.videoHeight
        if (!vw || !vh) return null
        const W = Math.min(640, vw)
        const H = Math.round((W * vh) / vw)
        let c = countCanvasRef.current
        if (!c) {
            c = document.createElement('canvas')
            countCanvasRef.current = c
        }
        if (c.width !== W || c.height !== H) {
            c.width = W
            c.height = H
        }
        const ctx = c.getContext('2d', { willReadFrequently: true })
        ctx.drawImage(video, 0, 0, W, H)
        return ctx.getImageData(0, 0, W, H)
    }
    const faceTrackPrevRef = useRef(null)
    const faceDetectorRef = useRef(null)
    const samplerRef = useRef(null)
    const motionRef = useRef(null)
    const motionLastRef = useRef(0)
    const speechMatchRef = useRef(null)
    const usbValueRef = useRef(0)
    const recentSpeechRef = useRef(null)
    // Spoken-keyword detection (Web Speech API) for the Microphone source.
    const speechRecogRef = useRef(null)
    const spokenWordRef = useRef(null)
    // Running speech-to-text transcript (all finalised speech) + the live line
    // (transcript + current interim), for on-screen captions / a Text output.
    const transcriptRef = useRef('')
    const liveTranscriptRef = useRef(null)

    // Laptop input triggers: currently pressed keyboard keys, mouse buttons and
    // on-screen buttons. Read every tick by the KEY_PRESS / MOUSE_CLICK /
    // SCREEN_BUTTON condition sources.
    const keysDownRef = useRef(new Set())
    const mouseButtonsRef = useRef(new Set())
    const screenButtonsRef = useRef({})
    // Rising-edge press count per on-screen button (incremented on each press).
    // A SCREEN_BUTTON decision fires once per new press instead of continuously
    // while held, so "if pressed -> counter +1" adds exactly one per click.
    const pressCountRef = useRef({})
    // Last press count each if-node has already acted on: `${nodeId}:${btnId}`.
    const consumedPressRef = useRef({})
    // Display outputs whose text is bound to a live value (a variable or the
    // object count), refreshed every frame: { [outputId]: action }.
    const liveTextBindingsRef = useRef({})
    // On-screen latching toggles (persistent on/off), read by the SCREEN_TOGGLE
    // condition source. `toggleStates` mirrors the ref so the switch re-renders.
    const screenTogglesRef = useRef({})
    const [toggleStates, setToggleStates] = useState({})
    // AI-mode counters/flags (Bicounter1/2, Biflag1/2). Live values read by the
    // VAR:<id> decision source and written by "variable" output actions.
    const varsRef = useRef({})
    const flipToggle = (id) => {
        const next = !screenTogglesRef.current[id]
        screenTogglesRef.current[id] = next
        setToggleStates((s) => ({ ...s, [id]: next }))
    }

    // Path crossing (pre-drawn in Configure > Lines) + trajectory overlay.
    // Paths are read at start; crossing state is kept per (pathId, target-id)
    // and completed crossings are logged/pulsed for the PATH_CROSS rule.
    const trailCanvasRef = useRef(null)
    const trailRef = useRef([])
    const prevFaceRef = useRef(null)
    const pathsRef = useRef([])
    const pathStatesRef = useRef({}) // `${pathId}:${targetId}` -> progress state
    const pathEventsRef = useRef([]) // recent { pathId, dir, class, trainedKey, at }
    const crossLogRef = useRef([])
    const crossTotalRef = useRef(0)
    const crossCountRef = useRef(0)
    const tickBusyRef = useRef(false)
    const [crossInfo, setCrossInfo] = useState(null)

    // Detection stats / log overlay (confidence etc.) with a show/hide toggle.
    const [showStats, setShowStats] = useState(false)
    const [stats, setStats] = useState([])
    const lastTickTimeRef = useRef(0)
    const fpsEmaRef = useRef(0)

    // Overlay toggles. Trail (short, fading) and Trajectory (full path, no fade
    // until stopped/toggled) are independent and OFF by default. The ROI region
    // overlay defaults ON only when a rule actually uses a region.
    const anyRegionRule = () =>
        Object.values(readJSON('allDetails', {})).some(
            (c) =>
                c &&
                c.selectedRegionId &&
                c.selectedRegionId !== 'none' &&
                c.selectedRegionId !== ''
        )
    const [showTrail, setShowTrail] = useState(
        () => sessionStorage.getItem('aiShowTrail') === 'true'
    )
    const [showTrajectory, setShowTrajectory] = useState(
        () => sessionStorage.getItem('aiShowTrajectory') === 'true'
    )
    const [showRegion, setShowRegion] = useState(() => {
        const stored = sessionStorage.getItem('aiShowRegion')
        if (stored === 'true') return true
        if (stored === 'false') return false
        return anyRegionRule()
    })
    // Crossing-line overlay + detection boxes visibility (default ON). These
    // only hide the drawing; detection/rules keep running underneath.
    const [showLine, setShowLine] = useState(
        () => sessionStorage.getItem('aiShowLine') !== 'false'
    )
    const [showBoxes, setShowBoxes] = useState(
        () => sessionStorage.getItem('aiShowBoxes') !== 'false'
    )
    // Numbered markers on each counted object (independent of Detection boxes).
    const [showCount, setShowCount] = useState(
        () => sessionStorage.getItem('aiShowCount') !== 'false'
    )
    const showTrailRef = useRef(showTrail)
    const showTrajectoryRef = useRef(showTrajectory)
    const showRegionRef = useRef(showRegion)
    const showLineRef = useRef(showLine)
    const showBoxesRef = useRef(showBoxes)
    const showCountRef = useRef(showCount)
    const trackingSelectedRef = useRef(trackingSelected)
    const regionsToDrawRef = useRef([])
    useEffect(() => {
        trackingSelectedRef.current = trackingSelected
    }, [trackingSelected])
    useEffect(() => {
        showTrailRef.current = showTrail
    }, [showTrail])
    useEffect(() => {
        showTrajectoryRef.current = showTrajectory
    }, [showTrajectory])
    useEffect(() => {
        showRegionRef.current = showRegion
    }, [showRegion])
    useEffect(() => {
        showLineRef.current = showLine
    }, [showLine])
    useEffect(() => {
        showBoxesRef.current = showBoxes
    }, [showBoxes])
    useEffect(() => {
        showCountRef.current = showCount
    }, [showCount])

    const toggleTrail = (n) => {
        sessionStorage.setItem('aiShowTrail', String(n))
        showTrailRef.current = n
        // Start a fresh path when either overlay is switched.
        trailRef.current = []
        setShowTrail(n)
    }
    const toggleTrajectory = (n) => {
        sessionStorage.setItem('aiShowTrajectory', String(n))
        showTrajectoryRef.current = n
        trailRef.current = []
        setShowTrajectory(n)
    }
    const toggleRegion = (n) => {
        sessionStorage.setItem('aiShowRegion', String(n))
        showRegionRef.current = n
        setShowRegion(n)
    }
    const toggleLine = (n) => {
        sessionStorage.setItem('aiShowLine', String(n))
        showLineRef.current = n
        setShowLine(n)
    }
    const toggleBoxes = (n) => {
        sessionStorage.setItem('aiShowBoxes', String(n))
        showBoxesRef.current = n
        setShowBoxes(n)
    }
    const toggleCount = (n) => {
        sessionStorage.setItem('aiShowCount', String(n))
        showCountRef.current = n
        setShowCount(n)
    }

    // Crossing-line editing on the play screen: click to add points / drag to
    // move, previewed with a ghost curve, persisted to the same store the
    // Configure Lines tab uses. `lineDrawStart` drives the intro draw-on.
    const [lineEditMode, setLineEditMode] = useState(false)
    const lineEditRef = useRef(false)
    const editCursorRef = useRef(null)
    const editDragRef = useRef(null)
    const lineDrawStartRef = useRef(0)
    const cameraWidgetRef = useRef(null)
    useEffect(() => {
        lineEditRef.current = lineEditMode
    }, [lineEditMode])

    // Trajectory tracking is locked to one DeepSORT id chosen from the live
    // detections; empty string = auto (face / largest object).
    const [selectedObjId, setSelectedObjId] = useState('')
    const selectedObjIdRef = useRef('')
    const [detectedIds, setDetectedIds] = useState([])
    const detectedIdsKeyRef = useRef('')
    useEffect(() => {
        selectedObjIdRef.current = selectedObjId
    }, [selectedObjId])
    const selectObj = (id) => {
        trailRef.current = []
        setSelectedObjId(id)
    }

    const resetPathState = () => {
        pathStatesRef.current = {}
    }
    const ensureEditPath = () => {
        if (!pathsRef.current.length) {
            pathsRef.current = [
                {
                    id: Math.random().toString(36).slice(2, 8),
                    name: 'Path 1',
                    points: [],
                },
            ]
        }
        return pathsRef.current[0]
    }
    const editWidgetRect = () => {
        const el = cameraWidgetRef.current
        return el ? el.getBoundingClientRect() : null
    }
    // object-fit: contain transform between the video's native pixels and a
    // w×h area. Contain shows the WHOLE frame (letterboxed) regardless of the
    // widget's shape, so a normalized (0-1) point maps to the same image content
    // everywhere — the line drawn in the Configure Lines tab (also contain)
    // lands in the identical spot here. Shared by the overlay draw and the edit
    // pointer map.
    const videoFitGeom = (w, h) => {
        const v = videoRef.current
        const vw = (v && v.videoWidth) || w
        const vh = (v && v.videoHeight) || h
        const scale = Math.min(w / vw, h / vh)
        const dispW = vw * scale
        const dispH = vh * scale
        return { dispW, dispH, offX: (w - dispW) / 2, offY: (h - dispH) / 2 }
    }
    const editToNorm = (clientX, clientY) => {
        const r = editWidgetRect()
        if (!r || !r.width || !r.height) return null
        const g = videoFitGeom(r.width, r.height)
        return {
            x: Math.min(1, Math.max(0, (clientX - r.left - g.offX) / g.dispW)),
            y: Math.min(1, Math.max(0, (clientY - r.top - g.offY) / g.dispH)),
        }
    }
    const hitEditPoint = (norm, r) => {
        const p = pathsRef.current[0]
        if (!p) return -1
        const g = videoFitGeom(r.width, r.height)
        const cx = norm.x * g.dispW + g.offX
        const cy = norm.y * g.dispH + g.offY
        for (let i = 0; i < p.points.length; i++) {
            const px = p.points[i].x * g.dispW + g.offX
            const py = p.points[i].y * g.dispH + g.offY
            if (Math.hypot(px - cx, py - cy) < 16) return i
        }
        return -1
    }
    const onEditDown = (e) => {
        const r = editWidgetRect()
        const norm = editToNorm(e.clientX, e.clientY)
        if (!r || !norm) return
        e.currentTarget.setPointerCapture?.(e.pointerId)
        ensureEditPath()
        const hit = hitEditPoint(norm, r)
        if (hit >= 0) {
            editDragRef.current = hit
            return
        }
        pathsRef.current[0].points.push(norm)
        savePaths(pathsRef.current)
        resetPathState()
    }
    const onEditMove = (e) => {
        const norm = editToNorm(e.clientX, e.clientY)
        if (!norm) return
        editCursorRef.current = norm
        if (editDragRef.current != null && pathsRef.current[0]) {
            pathsRef.current[0].points[editDragRef.current] = norm
            savePaths(pathsRef.current)
        }
    }
    const onEditUp = () => {
        editDragRef.current = null
    }
    const onEditLeave = () => {
        editCursorRef.current = null
    }
    const undoEditPoint = () => {
        const p = pathsRef.current[0]
        if (!p || !p.points.length) return
        p.points = p.points.slice(0, -1)
        savePaths(pathsRef.current)
        resetPathState()
    }
    const clearEditPath = () => {
        const p = pathsRef.current[0]
        if (!p) return
        p.points = []
        savePaths(pathsRef.current)
        resetPathState()
    }

    // Intro draw-on progress (0-1) for the crossing line; 1 while editing.
    const lineProgress = () => {
        if (lineEditRef.current || !lineDrawStartRef.current) return 1
        const p = (performance.now() - lineDrawStartRef.current) / 900
        return p >= 1 ? 1 : p
    }

    // Redraw the crossing overlay while editing but not running (when running,
    // detectTick's drawTrail already repaints every tick).
    const renderEditOverlay = () => {
        const canvas = trailCanvasRef.current
        const el = cameraWidgetRef.current || videoRef.current
        if (!canvas || !el) return
        const rect = el.getBoundingClientRect()
        const dpr = window.devicePixelRatio || 1
        const w = Math.round(rect.width)
        const h = Math.round(rect.height)
        if (!w || !h) return
        if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
            canvas.width = w * dpr
            canvas.height = h * dpr
        }
        canvas.style.width = `${w}px`
        canvas.style.height = `${h}px`
        const ctx = canvas.getContext('2d')
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        ctx.clearRect(0, 0, w, h)
        const g = videoFitGeom(w, h)
        drawPaths(
            ctx,
            pathsRef.current,
            (n) => ({ x: n.x * g.dispW + g.offX, y: n.y * g.dispH + g.offY }),
            {
                ghostCursor: editCursorRef.current,
                showIndices: true,
                progress: 1,
            }
        )
    }
    useEffect(() => {
        if (!lineEditMode) return
        let raf
        const loop = () => {
            raf = requestAnimationFrame(loop)
            if (!runningRef.current) renderEditOverlay()
        }
        raf = requestAnimationFrame(loop)
        return () => raf && cancelAnimationFrame(raf)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lineEditMode])

    // Draw the trajectory/trail, the ROI regions, and the pre-drawn crossing
    // paths (from Configure > Lines) over the camera widget.
    const drawTrail = () => {
        const canvas = trailCanvasRef.current
        const video = videoRef.current
        if (!canvas || !video) return
        const rect = video.getBoundingClientRect()
        const dpr = window.devicePixelRatio || 1
        const w = Math.round(rect.width)
        const h = Math.round(rect.height)
        if (!w || !h) return
        if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
            canvas.width = w * dpr
            canvas.height = h * dpr
        }
        canvas.style.width = `${w}px`
        canvas.style.height = `${h}px`
        const ctx = canvas.getContext('2d')
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        ctx.clearRect(0, 0, w, h)

        // ROI region rectangles (drawn when enabled and a rule uses a region).
        if (showRegionRef.current) {
            ctx.strokeStyle = '#ffd166'
            ctx.lineWidth = 2
            ctx.setLineDash([6, 4])
            ctx.font = '600 12px Halcyon_SemiBold, sans-serif'
            ctx.fillStyle = '#ffd166'
            regionsToDrawRef.current.forEach((r) => {
                const rx = (r.xPercent || 0) * w
                const ry = (r.yPercent || 0) * h
                const rw = (r.widthPercent || 0) * w
                const rh = (r.heightPercent || 0) * h
                ctx.strokeRect(rx, ry, rw, rh)
                if (r.name) ctx.fillText(r.name, rx + 4, ry + 14)
            })
            ctx.setLineDash([])
        }

        const pts = trailRef.current
        const now = Date.now()
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'

        // Video-content transform (object-fit: contain): overlays are placed
        // relative to the actual camera image, so a line drawn in the Configure
        // Lines tab lands in the identical spot here regardless of the camera
        // widget's aspect ratio.
        const g = videoFitGeom(w, h)
        const mapN = (nx, ny) => ({
            x: nx * g.dispW + g.offX,
            y: ny * g.dispH + g.offY,
        })

        // Trajectory: the full path as a smooth bezier (quadratic segments
        // through midpoints), solid until stopped/toggled. Tracking-only.
        if (
            trackingSelectedRef.current &&
            showTrajectoryRef.current &&
            pts.length > 1
        ) {
            ctx.lineWidth = 3
            ctx.strokeStyle = '#9b6ef3'
            ctx.beginPath()
            const p0 = mapN(pts[0].x / 100, pts[0].y / 100)
            ctx.moveTo(p0.x, p0.y)
            if (pts.length > 2) {
                for (let i = 1; i < pts.length - 1; i++) {
                    const c = mapN(pts[i].x / 100, pts[i].y / 100)
                    const m = mapN(
                        (pts[i].x + pts[i + 1].x) / 2 / 100,
                        (pts[i].y + pts[i + 1].y) / 2 / 100
                    )
                    ctx.quadraticCurveTo(c.x, c.y, m.x, m.y)
                }
                const last = mapN(
                    pts[pts.length - 1].x / 100,
                    pts[pts.length - 1].y / 100
                )
                ctx.lineTo(last.x, last.y)
            } else {
                const p1 = mapN(pts[1].x / 100, pts[1].y / 100)
                ctx.lineTo(p1.x, p1.y)
            }
            ctx.stroke()
        }

        // Trail: only the last ~2.5s, fading out.
        if (showTrailRef.current) {
            ctx.lineWidth = 4
            for (let i = 1; i < pts.length; i++) {
                const p1 = pts[i]
                const age = now - p1.t
                if (age > 2500) continue
                const alpha = Math.max(0, 1 - age / 2500)
                ctx.strokeStyle = `rgba(74,159,224,${alpha})`
                const a = mapN(pts[i - 1].x / 100, pts[i - 1].y / 100)
                const b = mapN(p1.x / 100, p1.y / 100)
                ctx.beginPath()
                ctx.moveTo(a.x, a.y)
                ctx.lineTo(b.x, b.y)
                ctx.stroke()
            }
        }

        // Head dot for whichever path overlay is active.
        if ((showTrailRef.current || showTrajectoryRef.current) && pts.length) {
            const head = pts[pts.length - 1]
            const hd = mapN(head.x / 100, head.y / 100)
            ctx.fillStyle = showTrajectoryRef.current ? '#9b6ef3' : '#4a9fe0'
            ctx.beginPath()
            ctx.arc(hd.x, hd.y, 5, 0, Math.PI * 2)
            ctx.fill()
        }

        // Detection boxes (object + face), mapped through the same transform so
        // they stay on the video content instead of floating over the letterbox.
        ctx.font = '600 12px Halcyon_SemiBold, sans-serif'
        ctx.textBaseline = 'bottom'
        const drawBox = (b, color) => {
            const tl = mapN(b.x / 100, b.y / 100)
            const bw = (b.w / 100) * g.dispW
            const bh = (b.h / 100) * g.dispH
            ctx.strokeStyle = color
            ctx.lineWidth = 2
            ctx.strokeRect(tl.x, tl.y, bw, bh)
            if (b.label) {
                const padX = 5
                const tagH = 18
                const tw = ctx.measureText(b.label).width
                const tagY = tl.y - tagH < 0 ? tl.y + bh : tl.y - tagH
                ctx.fillStyle = color
                ctx.fillRect(tl.x - 1, tagY, tw + padX * 2, tagH)
                ctx.fillStyle = '#ffffff'
                ctx.fillText(b.label, tl.x + padX, tagY + tagH - 3)
            }
        }
        if (showBoxesRef.current) {
            objectBoxesRef.current.forEach((b) => drawBox(b, '#3b82f6'))
            const fb = faceBoxRef.current
            if (fb) drawBox({ ...fb, label: `x:${fb.cx} y:${fb.cy}` }, '#4cc9a0')
        }

        // Live part-count overlay: each counted blob gets a numbered marker
        // (circle + index) so it's clear which objects are counted, plus a total
        // badge. Toggled by the "Count markers" switch.
        if (countPartsEnabledRef.current) {
            if (showCountRef.current) {
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                partBoxesRef.current.forEach((b, i) => {
                    const c = mapN(b.x + b.w / 2, b.y + b.h / 2)
                    ctx.beginPath()
                    ctx.arc(c.x, c.y, 12, 0, Math.PI * 2)
                    ctx.fillStyle = 'rgba(34,211,238,0.85)'
                    ctx.fill()
                    ctx.lineWidth = 2
                    ctx.strokeStyle = '#0b3b45'
                    ctx.stroke()
                    ctx.fillStyle = '#04222a'
                    ctx.font = '700 12px Halcyon_SemiBold, sans-serif'
                    ctx.fillText(String(i + 1), c.x, c.y + 1)
                })
                ctx.textAlign = 'left'
                ctx.textBaseline = 'alphabetic'
            }
            const label = `Parts: ${partCountRef.current}`
            ctx.font = '700 18px Halcyon_SemiBold, sans-serif'
            ctx.textBaseline = 'top'
            const tw = ctx.measureText(label).width
            ctx.fillStyle = 'rgba(0,0,0,0.6)'
            ctx.fillRect(8, 8, tw + 20, 30)
            ctx.fillStyle = '#22d3ee'
            ctx.fillText(label, 18, 15)
        }

        // Crossing paths (Configure > Lines, or drawn here) in the glowing
        // gate-line + direction-arrow style. Hidden when the Line toggle is off,
        // but always shown while editing so you can see what you draw. Only when
        // the Tracking feature is selected — a stale line must not linger after
        // the feature is turned off on the Select Feature screen.
        if (
            trackingSelectedRef.current &&
            (showLineRef.current || lineEditRef.current)
        ) {
            drawPaths(ctx, pathsRef.current, (n) => mapN(n.x, n.y), {
                progress: lineProgress(),
                ghostCursor: lineEditRef.current ? editCursorRef.current : null,
                showIndices: lineEditRef.current,
            })
        }
    }

    // A recognised audio class stays "true" for a short window so a transient
    // sound/word can satisfy an if-block that is checked every tick.
    useEffect(() => {
        speechMatchRef.current = (cfg) => {
            const r = recentSpeechRef.current
            if (!r || Date.now() - r.at > 900) return 0
            const classOk =
                !cfg.selectedClassId ||
                cfg.selectedClassId === 'none' ||
                String(r.classId) === String(cfg.selectedClassId)
            return classOk ? 1 : 0
        }
    }, [])

    // Live audio recognition (sound events / speaker classes) reusing the
    // AppMode teachable-machine audio runtime. Feeds the same if-block pipeline
    // via recentSpeechRef; it self-manages the mic listen loop with `running`.
    useAudioModel({
        appModeStorage: props.appModeStorage,
        worker: props.worker,
        allDetails: {},
        isPlay: running,
        enable,
        getByID: undefined,
        onAction: () => { },
        onRecognize: (classId, className) => {
            recentSpeechRef.current = { classId, className, at: Date.now() }
        },
    })

    const currentModel = (props.camModels || []).find(
        (m) => m.modelId === props.currentModelId
    )

    // (model, type) pairs referenced by the program's trained-recognition
    // rules. Each pair has its own head at `${modelId}__${type}`. Falls back to
    // the current model so a single-model program still works.
    const getReferencedModelKeys = () => {
        const all = readJSON('allDetails', {})
        const RECOGNITION_SOURCES = [
            'RECOGNIZE_FACE',
            'RECOGNIZE_OBJECT',
            'RECOGNIZE_POSE',
            'RECOGNIZE_HAND',
            'PART_COUNT',
        ]
        const map = new Map()
        Object.values(all).forEach((cfg) => {
            if (
                cfg &&
                RECOGNITION_SOURCES.includes(cfg.source) &&
                cfg.modelId
            ) {
                const type = sourceToType(cfg.source)
                const key = `${cfg.modelId}__${type}`
                map.set(key, { modelId: String(cfg.modelId), type, key })
            }
        })
        // Fall back to the current model only when the program actually has a
        // recognition rule (one that just lacks an explicit modelId). A program
        // with no recognition rules (e.g. only counters/flags/motion/buttons)
        // needs no trained model, so it must not touch IndexedDB at all.
        const hasRecognitionRule = Object.values(all).some(
            (cfg) => cfg && RECOGNITION_SOURCES.includes(cfg.source)
        )
        if (!map.size && hasRecognitionRule && props.currentModelId) {
            const m = (props.camModels || []).find(
                (x) => String(x.modelId) === String(props.currentModelId)
            )
            const type = m?.modelType || 'face'
            const key = `${props.currentModelId}__${type}`
            map.set(key, {
                modelId: String(props.currentModelId),
                type,
                key,
            })
        }
        return Array.from(map.values())
    }

    // Recognition type -> inference architecture.
    const archOfType = (t) =>
        t === 'pose' ? 'pose' : t === 'gesture' ? 'hand' : 'image'

    const loadModels = async () => {
        const camModels = props.camModels || []
        const refs = getReferencedModelKeys()
        const modelFor = (id) =>
            camModels.find((m) => String(m.modelId) === String(id))

        const needImage = refs.some((r) => archOfType(r.type) === 'image')
        const needPose = refs.some((r) => archOfType(r.type) === 'pose')
        const needHand = refs.some((r) => archOfType(r.type) === 'hand')

        // Shared feature extractors, loaded once and only if needed.
        if (needImage && !mobilenetRef.current) {
            try {
                mobilenetRef.current = await tf.loadGraphModel(MOBILENET_URL, {
                    fromTFHub: true,
                })
            } catch (e) {
                console.warn('[aiCode] mobilenet load failed', e)
            }
        }
        if (needPose && !poseModelRef.current) {
            try {
                // MediaPipe Pose Landmarker (33 landmarks) replaces MoveNet.
                poseModelRef.current = await getPoseLandmarker()
            } catch (e) {
                console.warn('[aiCode] pose landmarker load failed', e)
            }
        }

        // Models currently saved in IndexedDB. Checked before loading, because a
        // missing store makes tf.loadLayersModel throw an UNCATCHABLE error from
        // inside its own IndexedDB success handler (not the awaited promise).
        // Only queried when a trained head is actually referenced — a program
        // with no recognition rules (e.g. only counters/flags/motion/buttons)
        // must not touch the model store at all.
        let storedModels = {}
        if (refs.length) {
            try {
                storedModels = await safeListModels()
            } catch (e) {
                /* ignore — treat as none stored */
            }
        }

        // Each referenced (model, type): load its per-type trained head and the
        // classes belonging to that type only.
        for (const r of refs) {
            if (modelsRef.current[r.key]) continue
            const m = modelFor(r.modelId)
            if (!m) continue
            if (!storedModels[`indexeddb://${r.key}`]) {
                console.warn('[aiCode] trained head not saved, skipping', r.key)
                continue
            }
            try {
                const head = await tf.loadLayersModel(`indexeddb://${r.key}`)
                const typedClasses = Object.values(m.ImageClasses || {}).filter(
                    (c) => (c.classType || 'face') === r.type
                )
                modelsRef.current[r.key] = {
                    type: archOfType(r.type),
                    // 'object' models are localized (boxed); 'face' whole-frame.
                    modelType: r.type,
                    head,
                    classNames: typedClasses.map((c) => c.className),
                    classIds: typedClasses.map((c) => c.id),
                }
                console.log(
                    '[aiCode] trained head loaded:',
                    m.modelName,
                    r.type
                )
            } catch (e) {
                console.warn('[aiCode] trained head load failed', r.key, e)
            }
        }

        // MediaPipe hand recognizer — needed for built-in gestures AND for any
        // trained gesture (hand-landmark) model.
        if (
            (selectedFeatures.includes('ai/gesture') || needHand) &&
            !gestureRecognizerRef.current
        ) {
            try {
                gestureRecognizerRef.current = await getGestureRecognizer()
            } catch (e) {
                console.warn('[aiCode] gesture recognizer load failed', e)
            }
        }

        // Object detector + DeepSORT-lite tracker for object tracking and
        // object line-crossing. The detector comes from the shared provider
        // (swap COCO->YOLO in vision/detectionProvider.js). Loaded only when a
        // rule needs objects.
        const all = readJSON('allDetails', {})
        const needsObjects = Object.values(all).some(
            (cfg) => cfg && cfg.source === 'PATH_CROSS'
        )
        if (needsObjects && !cocoModelRef.current) {
            try {
                cocoModelRef.current = await getDetector()
            } catch (e) {
                console.warn('[aiCode] object detector load failed', e)
            }
        }
        if (needsObjects && !objTrackerRef.current) {
            objTrackerRef.current = new ObjectTracker({ maxAge: 30, minHits: 2 })
        }

        // Face tracker (face-api) supplies face position/size for tracking,
        // line-crossing and the presence fallback. Loaded whenever the camera
        // is in use so the stats/coords always populate.
        if (cameraEnabled && !faceDetectorRef.current) {
            try {
                const tracker = createFaceTracker()
                await tracker.load()
                faceDetectorRef.current = tracker
            } catch (e) {
                console.warn('[aiCode] face tracker load failed', e)
                faceDetectorRef.current = null
            }
        }
    }

    // Build this tick's perception: trained recognition, face tracking, a
    // downscaled frame (shared by colour + motion) and the motion level.
    const runDetection = async () => {
        const regionsByModel = {}
            ; (props.camModels || []).forEach((m) => {
                if (m && m.roiRegions)
                    regionsByModel[String(m.modelId)] = m.roiRegions
            })
        const p = {
            recognition: null,
            recognitions: {},
            gesture: null,
            objects: [],
            face: { present: 0, x: 0, y: 0, size: 0, speed: 0 },
            motion: { level: 0, box: null, moving: false },
            frame: null,
            regions: currentModel?.roiRegions || [],
            regionsByModel,
            speech: speechMatchRef.current,
            spokenWord: spokenWordRef.current,
            usb: usbValueRef.current,
            keys: keysDownRef.current,
            mouse: mouseButtonsRef.current,
            buttons: screenButtonsRef.current,
            toggles: screenTogglesRef.current,
            vars: varsRef.current,
            ocrText: ocrTextRef.current,
            markers: markersRef.current,
            speechTranscript:
                (liveTranscriptRef.current &&
                    liveTranscriptRef.current.text) ||
                '',
        }
        const video = videoRef.current
        if (!video || video.readyState !== 4) {
            perceptionRef.current = p
            return p
        }

        const models = modelsRef.current
        const ids = Object.keys(models)
        // Face image models classify the whole frame; object image models are
        // localized (boxed) via the sliding-window scanner below.
        const imageIds = ids.filter(
            (id) => models[id].type === 'image' && models[id].modelType !== 'object'
        )
        const objectModelIds = ids.filter(
            (id) => models[id].type === 'image' && models[id].modelType === 'object'
        )
        const knnIds = ids.filter((id) => models[id].type === 'knn')
        const poseIds = ids.filter((id) => models[id].type === 'pose')
        const handIds = ids.filter((id) => models[id].type === 'hand')

        const argmax = (arr) => {
            let top = 0
            for (let i = 1; i < arr.length; i++) if (arr[i] > arr[top]) top = i
            return top
        }

        // Image recognition: extract MobileNet features once and share them
        // across every CNN head (per-node models) and every KNN model (cosine
        // match against class centroids).
        if ((imageIds.length || knnIds.length) && mobilenetRef.current) {
            try {
                // Squeezed 1-D feature (for KNN) and its batched form (for CNN
                // heads). Both disposed after use — a leaked expandDims per tick
                // was blowing up GPU memory and hanging the page.
                const featVec = tf.tidy(() => {
                    const t = tf.browser.fromPixels(video)
                    const r = tf.image
                        .resizeBilinear(t, [224, 224], true)
                        .div(255)
                    return mobilenetRef.current.predict(r.expandDims()).squeeze()
                })
                const featsBatched = knnIds.length
                    ? null
                    : featVec.expandDims()

                for (const id of imageIds) {
                    const batched = featsBatched || featVec.expandDims()
                    const probs = tf.tidy(() => models[id].head.predict(batched))
                    const data = await probs.data()
                    probs.dispose()
                    if (!featsBatched) batched.dispose()
                    const top = argmax(data)
                    p.recognitions[id] = {
                        conf: data[top],
                        classId: models[id].classIds[top],
                        className: models[id].classNames[top],
                    }
                }

                if (knnIds.length) {
                    const norm = l2normalize(Array.from(await featVec.data()))
                    for (const id of knnIds) {
                        const cents = models[id].centroids
                        let bestIdx = 0
                        let bestSim = -2
                        cents.forEach((c, i) => {
                            const sim = dot(norm, c.vector)
                            if (sim > bestSim) {
                                bestSim = sim
                                bestIdx = i
                            }
                        })
                        p.recognitions[id] = {
                            // Map cosine [-1,1] -> confidence [0,1].
                            conf: (bestSim + 1) / 2,
                            classId: cents[bestIdx].classId,
                            className: cents[bestIdx].className,
                        }
                    }
                }

                if (featsBatched) featsBatched.dispose()
                featVec.dispose()
            } catch (e) {
                /* ignore frame errors */
            }
        }

        // Pose recognition: MoveNet keypoints -> hip-centred, torso-scaled
        // 34-dim vector -> each pose model's trained classifier.
        if (poseIds.length && poseModelRef.current) {
            try {
                const res = poseModelRef.current.detectForVideo(
                    video,
                    nextVideoTimestamp(performance.now())
                )
                const pv = poseVectorFromResult(res)
                if (pv && pv.vector) {
                    const vec = pv.vector
                    for (const id of poseIds) {
                        const probs = tf.tidy(() =>
                            models[id].head.predict(tf.tensor2d([vec]))
                        )
                        const data = await probs.data()
                        probs.dispose()
                        const top = argmax(data)
                        p.recognitions[id] = {
                            conf: data[top],
                            classId: models[id].classIds[top],
                            className: models[id].classNames[top],
                        }
                    }
                }
            } catch (e) {
                /* ignore frame errors */
            }
        }

        // Custom object detection: localize each trained object model with the
        // sliding-window scanner (throttled) so we get a real bounding box + the
        // custom label, not just whole-frame classification.
        if (objectModelIds.length && mobilenetRef.current) {
            const nowL = performance.now()
            if (nowL - objLocalizeAtRef.current > 180) {
                objLocalizeAtRef.current = nowL
                const found = []
                for (const id of objectModelIds) {
                    try {
                        const dets = await localizeObjects({
                            video,
                            mobilenet: mobilenetRef.current,
                            head: models[id].head,
                            classNames: models[id].classNames,
                            classIds: models[id].classIds,
                            minConf: 0.6,
                        })
                        // Best detection per model drives its recognition.
                        const best = dets.reduce(
                            (b, d) => (!b || d.conf > b.conf ? d : b),
                            null
                        )
                        if (best) {
                            p.recognitions[id] = {
                                conf: best.conf,
                                classId: best.classId,
                                className: best.className,
                                // Normalized 0-1 box, so a rule can require the
                                // object to be inside a drawn ROI region.
                                box: best.box,
                            }
                        }
                        dets.forEach((d) =>
                            found.push({
                                class: d.className,
                                cx: Math.round((d.box.x + d.box.w / 2) * 100),
                                cy: Math.round((d.box.y + d.box.h / 2) * 100),
                                size: Math.round(
                                    Math.min(
                                        100,
                                        Math.hypot(d.box.w * 100, d.box.h * 100)
                                    )
                                ),
                                speed: 0,
                                score: d.conf,
                                box: {
                                    x: d.box.x * 100,
                                    y: d.box.y * 100,
                                    w: d.box.w * 100,
                                    h: d.box.h * 100,
                                },
                            })
                        )
                    } catch (e) {
                        /* ignore frame errors */
                    }
                }
                customObjectsRef.current = found
            }
            // Carry recognition on held frames too (throttled localization).
            if (nowL - objLocalizeAtRef.current <= 180) {
                for (const id of objectModelIds) {
                    if (!p.recognitions[id] && models[id]._lastRec)
                        p.recognitions[id] = models[id]._lastRec
                }
            }
            objectModelIds.forEach((id) => {
                if (p.recognitions[id]) models[id]._lastRec = p.recognitions[id]
            })
        }

        // Live part counting: segment the dark parts on the light background and
        // count the blobs. Segmentation is the reliable signal for this clean,
        // high-contrast scene (the classifier trained on whole frames mismatches
        // tight crops, and skin/hand is too bright to be foreground). Throttled;
        // runs only when a PART_COUNT rule is present.
        if (countPartsEnabledRef.current) {
            const nowC = performance.now()
            if (nowC - partCountAtRef.current > 150) {
                partCountAtRef.current = nowC
                try {
                    const frameImg = grabCountFrame(video)
                    if (frameImg) {
                        const blobs = segmentBlobs(frameImg, {
                            threshold: 0.4,
                            minAreaFrac: 0.0004,
                            maxAreaFrac: 0.15,
                        })
                        const target = countTargetRef.current
                        const model =
                            target &&
                            target.classId &&
                            modelsRef.current[`${target.modelId}__object`]
                        if (model && model.head && mobilenetRef.current) {
                            // A class is chosen: classify blobs, count matches.
                            const res = await countParts({
                                video,
                                mobilenet: mobilenetRef.current,
                                model,
                                blobs,
                                targetClassId: target.classId,
                                minConf: 0.5,
                            })
                            partCountRef.current = res.count
                            partBoxesRef.current = res.boxes
                        } else {
                            // No class chosen: count every segmented blob.
                            partCountRef.current = blobs.length
                            partBoxesRef.current = blobs
                        }
                        console.log(
                            '[aiCode] part blobs:',
                            blobs.length,
                            'counted:',
                            partCountRef.current
                        )
                    }
                } catch (e) {
                    /* ignore frame errors */
                }
            }
        }
        p.partCount = partCountRef.current

        // Live OCR (Tesseract) — heavy, so throttle and run asynchronously
        // (never blocks the detection tick). Only when an OCR rule is present.
        if (
            ocrEnabledRef.current &&
            !ocrBusyRef.current &&
            video.videoWidth > 0 &&
            video.videoHeight > 0
        ) {
            const nowO = performance.now()
            if (nowO - ocrAtRef.current > 1500) {
                ocrAtRef.current = nowO
                const vw = video.videoWidth
                const vh = video.videoHeight
                // Upscale small frames — Tesseract reads larger text far better.
                const W = Math.min(1280, Math.max(720, vw))
                const H = Math.round((W * vh) / vw)
                let c = ocrCanvasRef.current
                if (!c) {
                    c = document.createElement('canvas')
                    ocrCanvasRef.current = c
                }
                if (c.width !== W || c.height !== H) {
                    c.width = W
                    c.height = H
                }
                const octx = c.getContext('2d', { willReadFrequently: true })
                octx.drawImage(video, 0, 0, W, H)
                // Light grayscale preprocessing (shared with /vision OCR).
                preprocessCanvasForOcr(c)
                ocrBusyRef.current = true
                recognizeText(c)
                    .then((res) => {
                        // Only feed confident reads into the rules — a low-
                        // confidence frame is noise and would spam-trigger.
                        if (
                            res &&
                            res.confidence >= OCR_MIN_CONFIDENCE &&
                            res.text
                        )
                            ocrTextRef.current = {
                                text: res.text,
                                at: Date.now(),
                            }
                    })
                    .finally(() => {
                        ocrBusyRef.current = false
                    })
            }
        }

        // ArUco markers — throttled; the detector's first import is async, then
        // detection is fast. Uses the downscaled count frame (RGBA ImageData).
        if (markerEnabledRef.current && !markerBusyRef.current) {
            const nowM = performance.now()
            if (nowM - markerAtRef.current > 120) {
                markerAtRef.current = nowM
                const frameImg = grabCountFrame(video)
                if (frameImg) {
                    markerBusyRef.current = true
                    detectMarkerIds(frameImg)
                        .then((ids) => {
                            markersRef.current = { ids, at: Date.now() }
                        })
                        .finally(() => {
                            markerBusyRef.current = false
                        })
                }
            }
        }

        // One MediaPipe hand pass feeds BOTH the built-in gesture readout and
        // every trained gesture (hand-landmark) model. Skip frames with no real
        // dimensions — MediaPipe's hand ROI throws on a zero-size image.
        if (
            gestureRecognizerRef.current &&
            video.videoWidth > 0 &&
            video.videoHeight > 0
        ) {
            try {
                const ts = nextVideoTimestamp(performance.now())
                const gres = gestureRecognizerRef.current.recognizeForVideo(
                    video,
                    ts
                )
                const topG = gres?.gestures?.[0]?.[0]
                p.gesture =
                    topG && topG.categoryName !== 'None'
                        ? { name: topG.categoryName, conf: topG.score }
                        : { name: 'None', conf: 0 }

                // Trained gesture models: normalize the hand landmarks the same
                // way training did, then classify with each model's head.
                if (handIds.length) {
                    const hand = handVectorFromResult(gres)
                    if (hand && hand.vector) {
                        for (const id of handIds) {
                            const probs = tf.tidy(() =>
                                models[id].head.predict(
                                    tf.tensor2d([hand.vector])
                                )
                            )
                            const data = await probs.data()
                            probs.dispose()
                            const top = argmax(data)
                            p.recognitions[id] = {
                                conf: data[top],
                                classId: models[id].classIds[top],
                                className: models[id].classNames[top],
                            }
                        }
                    }
                }
            } catch (e) {
                /* ignore frame errors */
            }
        }

        // Default recognition for rules that don't pin a model (first loaded).
        p.recognition = p.recognitions[ids[0]] || null

        // Tracking (position/distance/speed) + presence via the face tracker.
        // The tracker returns the largest face in normalized (0-1) video space.
        if (faceDetectorRef.current) {
            try {
                const f = await faceDetectorRef.current.detect(video)
                if (f) {
                    const xPct = (f.x + f.w / 2) * 100
                    const yPct = (f.y + f.h / 2) * 100
                    const wPct = f.w * 100
                    const hPct = f.h * 100
                    // Distance ~ apparent size (bigger box = closer). Speed ~
                    // centre movement per second, both clamped to 0-100.
                    const size = Math.round(
                        Math.min(100, Math.hypot(wPct, hPct))
                    )
                    const nowP = performance.now()
                    let speed = 0
                    const prev = faceTrackPrevRef.current
                    if (prev) {
                        const dt = Math.max(1, nowP - prev.t) / 1000
                        speed = Math.round(
                            Math.min(
                                100,
                                Math.hypot(xPct - prev.x, yPct - prev.y) / dt
                            )
                        )
                    }
                    faceTrackPrevRef.current = { x: xPct, y: yPct, t: nowP }
                    p.face = {
                        present: 1,
                        x: Math.round(xPct),
                        y: Math.round(yPct),
                        size,
                        speed,
                    }
                    const fb = {
                        x: f.x * 100,
                        y: f.y * 100,
                        w: wPct,
                        h: hPct,
                        cx: Math.round(xPct),
                        cy: Math.round(yPct),
                    }
                    faceBoxRef.current = fb
                } else {
                    faceTrackPrevRef.current = null
                    faceBoxRef.current = null
                }
            } catch {
                /* ignore */
            }
        }

        // Object detection + DeepSORT-lite tracking. The detector (provider)
        // gives per-frame boxes; ObjectTracker assigns persistent ids and keeps
        // them stable across occlusion / multiple objects. Positions/size are
        // 0-100; speed comes from the tracked id's own motion.
        let cocoObjects = []
        if (cocoModelRef.current && objTrackerRef.current) {
            try {
                const results = await cocoModelRef.current.detect(video, 20)
                const vw = video.videoWidth || 1
                const vh = video.videoHeight || 1
                const dets = results
                    .filter((r) => r.score > 0.45)
                    .map((r) => ({
                        class: r.class,
                        score: r.score,
                        x: r.bbox[0] / vw,
                        y: r.bbox[1] / vh,
                        w: r.bbox[2] / vw,
                        h: r.bbox[3] / vh,
                    }))
                const frame = samplerRef.current
                    ? samplerRef.current.sample(video)
                    : null
                const tracks = objTrackerRef.current.update(dets, frame)
                const nowP = performance.now()
                cocoObjects = tracks.map((t) => {
                    const cxN = t.cx * 100
                    const cyN = t.cy * 100
                    let speed = 0
                    const prev = objPrevRef.current[t.id]
                    if (prev) {
                        const dt = Math.max(1, nowP - prev.t) / 1000
                        speed = Math.round(
                            Math.min(
                                100,
                                Math.hypot(cxN - prev.x, cyN - prev.y) / dt
                            )
                        )
                    }
                    objPrevRef.current[t.id] = { x: cxN, y: cyN, t: nowP }
                    return {
                        id: t.id,
                        class: t.class,
                        cx: Math.round(cxN),
                        cy: Math.round(cyN),
                        size: Math.round(
                            Math.min(100, Math.hypot(t.w * 100, t.h * 100))
                        ),
                        speed,
                        score: t.score,
                        box: {
                            x: t.x * 100,
                            y: t.y * 100,
                            w: t.w * 100,
                            h: t.h * 100,
                        },
                    }
                })
            } catch (e) {
                /* ignore frame errors */
            }
        }

        // Merge COCO tracking objects with custom-trained detections, then draw
        // one box + coord label per detected object.
        p.objects = [...cocoObjects, ...customObjectsRef.current]

        // Keep the trajectory id picker in sync with the live tracked ids.
        const idList = p.objects
            .filter((o) => o.id != null)
            .map((o) => ({ id: String(o.id), class: o.class }))
        const idKey = idList.map((o) => o.id).join(',')
        if (idKey !== detectedIdsKeyRef.current) {
            detectedIdsKeyRef.current = idKey
            setDetectedIds(idList)
        }

        // With trajectory locked to one id, show only that object's box.
        let boxObjects = p.objects.filter((o) => o.box)
        if (showTrajectoryRef.current && selectedObjIdRef.current) {
            boxObjects = boxObjects.filter(
                (o) => String(o.id) === selectedObjIdRef.current
            )
        }
        const boxes = boxObjects.map((o) => ({
            x: o.box.x,
            y: o.box.y,
            w: o.box.w,
            h: o.box.h,
            label: o.id
                ? `#${o.id} ${o.class} ${o.cx},${o.cy}`
                : `${o.class} ${o.cx},${o.cy}`,
        }))
        objectBoxesRef.current = boxes

        // One downscaled frame feeds both colour and motion detection.
        if (samplerRef.current) {
            try {
                const image = samplerRef.current.sample(video)
                p.frame = {
                    image,
                    w: samplerRef.current.width,
                    h: samplerRef.current.height,
                }
                // Live intensity (0-100% closeness) of each rule's target colour.
                if (colorEnabledRef.current)
                    colorReadoutsRef.current = colorRulesRef.current.map((r) => {
                        const res = detectColourRGB(
                            image,
                            p.frame.w,
                            p.frame.h,
                            r.target,
                            null
                        )
                        return {
                            name: r.name,
                            rgb: r.target.rgb,
                            intensity: distanceToIntensity(res.distance),
                            matched: res.matched,
                        }
                    })
                if (motionRef.current) {
                    p.motion = motionRef.current.analyse(
                        image,
                        p.frame.w,
                        p.frame.h,
                        { pixelThreshold: 25 },
                        null
                    )
                    // Debounced boolean: hold "moving" for 600ms after the last
                    // motion so hardware/screen output doesn't flicker.
                    const nowMs = performance.now()
                    if (p.motion.level >= 2) motionLastRef.current = nowMs
                    p.motion.moving = nowMs - motionLastRef.current < 600
                }
            } catch {
                /* ignore */
            }
        }

        // Movement trail (face) + line-crossing for each tracked target.
        const now = Date.now()

        // Build the list of tracked targets for path crossing: each detected
        // object (persistent id from DeepSORT) plus the face (id 'face'). Centres
        // are converted to 0-1 to match the path points.
        const targets = []
        p.objects.forEach((o) => {
            targets.push({
                id: 'obj' + o.id,
                cur: { x: o.cx / 100, y: o.cy / 100 },
                box: o.box
                    ? {
                        x: o.box.x / 100,
                        y: o.box.y / 100,
                        w: o.box.w / 100,
                        h: o.box.h / 100,
                    }
                    : null,
                class: o.class,
                trainedKey: o.trainedKey || null,
                label: `#${o.id} ${o.class}`,
            })
        })
        if (p.face.present) {
            targets.push({
                id: 'face',
                cur: { x: p.face.x / 100, y: p.face.y / 100 },
                box: null,
                class: 'person',
                trainedKey: p.faceTrainedKey || null,
                label: 'Face',
            })
        }

        // Advance each (path, target) route-following state; a target that
        // travels through every point in order is logged as 'passed'.
        const liveKeys = new Set()
        pathsRef.current.forEach((path) => {
            targets.forEach((t) => {
                const key = `${path.id}:${t.id}`
                liveKeys.add(key)
                if (!pathStatesRef.current[key])
                    pathStatesRef.current[key] = makePathProgress()
                const dir = stepPathProgress(
                    path,
                    pathStatesRef.current[key],
                    { box: t.box, x: t.cur.x, y: t.cur.y },
                    now
                )
                if (dir) {
                    pathEventsRef.current.unshift({
                        pathId: path.id,
                        dir,
                        class: t.class,
                        trainedKey: t.trainedKey,
                        at: now,
                    })
                    if (pathEventsRef.current.length > 20)
                        pathEventsRef.current.pop()
                    crossTotalRef.current += 1
                    crossLogRef.current.unshift({
                        label: `${t.label} · ${path.name}`,
                        dir,
                        at: now,
                    })
                    if (crossLogRef.current.length > 6)
                        crossLogRef.current.pop()
                }
            })
        })
        // Drop state for targets that disappeared.
        Object.keys(pathStatesRef.current).forEach((k) => {
            if (!liveKeys.has(k)) delete pathStatesRef.current[k]
        })
        // Expire stale events.
        pathEventsRef.current = pathEventsRef.current.filter(
            (e) => now - e.at < 1500
        )
        p.pathEvents = pathEventsRef.current
        // Ids of paths that currently exist, so a rule pinned to a deleted /
        // stale path id falls back to matching any path.
        p.pathIds = pathsRef.current.map((pp) => String(pp.id))

        // Trajectory/trail buffer. When an id is locked, follow ONLY that
        // object; otherwise fall back to the primary target (face, else largest
        // object). Fed while either overlay is on.
        if (showTrailRef.current || showTrajectoryRef.current) {
            let tp = null
            if (selectedObjIdRef.current) {
                const sel = p.objects.find(
                    (o) => String(o.id) === selectedObjIdRef.current
                )
                if (sel) tp = { x: sel.cx, y: sel.cy }
            } else {
                let largestObj = null
                p.objects.forEach((o) => {
                    if (!largestObj || o.size > largestObj.size) largestObj = o
                })
                tp = p.face.present
                    ? { x: p.face.x, y: p.face.y }
                    : largestObj
                        ? { x: largestObj.cx, y: largestObj.cy }
                        : null
            }
            if (tp) {
                trailRef.current.push({ x: tp.x, y: tp.y, t: now })
                if (trailRef.current.length > 1500) trailRef.current.shift()
            }
        }

        perceptionRef.current = p
        return p
    }

    // ---- Sequential program walker -------------------------------------
    // Runs the flowchart in order from `start`, honouring if / output / wait /
    // loop / end blocks. Detection runs continuously in detectTick; the walker
    // only reads the latest perception, so each decision samples a live frame.
    const firedOutputsRef = useRef({})
    const programStatusRef = useRef('')
    // Resolved object URLs for audio/video media stored in IndexedDB, keyed by
    // the action's mediaKey. Pre-loaded once per run (start) so playback/display
    // is synchronous; revoked on stop.
    const mediaUrlsRef = useRef({})

    // The playable/displayable source for a media action: an inline data URL
    // (images) or the object URL resolved from the IndexedDB blob (audio/video).
    const mediaSrcOf = (a) =>
        (a && a.src) || (a && a.mediaKey && mediaUrlsRef.current[a.mediaKey]) || ''

    // Latch an output node's display (persists until overwritten) and drive its
    // hardware: hardware only for hardware actions, display from whatever
    // content the action carries.
    // Play an audio action's clip. A fresh element per fire lets the same sound
    // replay (and overlap) each time the action runs; playback needs the Play
    // gesture that already started the run, so autoplay is allowed.
    const playAudio = (src) => {
        if (!src) return
        try {
            const el = new Audio(src)
            el.play().catch(() => { })
        } catch (e) {
            /* ignore */
        }
    }

    // Send the accumulated port-based live state as ONE batched live-write frame:
    // [ 'L','A', (char, portNum, value)…, 'E','R' ]. Chars match what the LS
    // settings packet declared, so the board interprets each port correctly.
    const flushLiveWrite = () => {
        if (!props.worker) return
        const state = liveStateRef.current
        const ports = Object.keys(state)
        if (!ports.length) return
        const data = [76, 65] // 'L','A'
        ports.forEach((portNum) => {
            const s = state[portNum]
            data.push(s.char & 0xff, Number(portNum) & 0xff, s.value & 0xff)
        })
        data.push(69, 82) // 'E','R'
        props.worker.postMessage({ type: 'writeArray', value: data })
    }

    const applyOutputActions = (nodeId, all) => {
        const acts = Array.isArray(all[nodeId]?.actions)
            ? all[nodeId].actions
            : []
        const outputs = { ...firedOutputsRef.current }
        const connectedDevice =
            sessionStorage.getItem('connectedDevice') || 'Ace'
        let hwChanged = false
        // Live sources an action can pull from: the object count and the
        // current counter/flag values.
        const varCtx = {
            count: partCountRef.current,
            vars: varsRef.current,
            transcript:
                (liveTranscriptRef.current &&
                    liveTranscriptRef.current.text) ||
                '',
        }
        acts.forEach((a) => {
            if (a.kind === 'hardware' && a.accessory === '__servo__') {
                sendServo(a.servoId, a.value)
            } else if (a.kind === 'hardware' && a.accessory && a.port) {
                // Accumulate into the batched live-write state instead of sending
                // a per-port packet, so every port in this block goes out in one
                // frame (bug 52).
                const char = compName_character[a.accessory]
                if (char != null && MOTOR_ACCS.includes(a.accessory)) {
                    // H-bridge: forward drives the forward pin, backward the
                    // reverse pin, 0 stops both (bug 53).
                    const [p1, p2] = motorPinPorts(connectedDevice, a.port)
                    const v = Number(a.value) || 0
                    const speed = motorSpeed(a.accessory, v)
                    if (p1 != null) {
                        liveStateRef.current[p1] = {
                            char,
                            value: v >= 0 ? speed : 0,
                        }
                        hwChanged = true
                    }
                    if (p2 != null) {
                        liveStateRef.current[p2] = {
                            char,
                            value: v < 0 ? speed : 0,
                        }
                        hwChanged = true
                    }
                } else if (char != null) {
                    const portNum =
                        livePortsNumber?.[connectedDevice]?.[a.port] ??
                        livePortsNumber.Ace?.[a.port]
                    if (portNum != null) {
                        liveStateRef.current[portNum] = {
                            char,
                            value: liveWriteValue(a.accessory, a.value),
                        }
                        hwChanged = true
                    }
                }
            }
            if (a.kind === 'variable' && a.varId) {
                // Assign/add/subtract a number, the live object count, or
                // another variable's value into the counter; set/toggle a flag.
                varsRef.current[a.varId] = applyVarOp(
                    varsRef.current[a.varId],
                    a,
                    varCtx
                )
            }
            if (a.kind === 'audio') {
                // Audio has no latched visual; just play the clip when it runs.
                playAudio(mediaSrcOf(a))
            } else if (a.outputId) {
                if (a.kind === 'text') {
                    // Text can show a typed literal or a live value (count/var).
                    const txt = resolveActionText(a, varCtx)
                    if (txt.length)
                        outputs[a.outputId] = { kind: 'text', text: txt }
                    // A value/count-bound text keeps refreshing every frame so
                    // it tracks the counter live, wherever the block sits.
                    if (a.mode === 'assign')
                        liveTextBindingsRef.current[a.outputId] = a
                    else delete liveTextBindingsRef.current[a.outputId]
                } else {
                    const src = mediaSrcOf(a)
                    if (src) outputs[a.outputId] = { kind: a.kind, src }
                }
            }
        })
        // One batched live-write frame for every port this block touched.
        if (hwChanged) flushLiveWrite()
        firedOutputsRef.current = outputs
        setFiredOutputs(outputs)
    }

    // Wait-block total (ms + s + min + hr), stored per node id in sessionStorage.
    const waitBlockMs = (id) => {
        const g = (k) => parseInt(sessionStorage.getItem(`${k}${id}`), 10) || 0
        return (
            g('milliSec') +
            g('sec') * 1000 +
            g('min') * 60000 +
            g('hr') * 3600000
        )
    }

    // Sleep that bails promptly when the program is stopped.
    const sleepUntil = (ms) =>
        new Promise((resolve) => {
            if (ms <= 0) return resolve()
            const end = performance.now() + ms
            const step = () => {
                if (!runningRef.current || performance.now() >= end)
                    return resolve()
                setTimeout(step, Math.min(50, end - performance.now()))
            }
            step()
        })
    const yieldFrame = () => new Promise((r) => setTimeout(r, 0))

    // Evaluate a decision, with rising-edge semantics for on-screen buttons: an
    // `if button pressed` node fires once per physical press (not every frame it
    // is held), so it can drive "counter +1" once per click. Consumption is
    // tracked per if-node so different nodes each see the press.
    const conditionHolds = (cfg, nodeId) => {
        if (cfg && cfg.source === 'DETECT_COLOR') {
            // Measure this frame, then require the match to hold for N frames in
            // a row (18.2 step 6) before the rule fires — one noisy frame can't
            // trigger a device action. Streak is tracked per if-node.
            const p = perceptionRef.current || {}
            const tgt = cfg.colorTarget
            let matched = false
            if (p.frame && tgt) {
                const res = detectColourRGB(
                    p.frame.image,
                    p.frame.w,
                    p.frame.h,
                    tgt,
                    null
                )
                matched = res.matched
            }
            const need = Math.max(1, Number(tgt?.consecutive ?? 3))
            const key = `${nodeId}:color`
            const streak = matched ? (colorStreakRef.current[key] || 0) + 1 : 0
            colorStreakRef.current[key] = streak
            const held = streak >= need
            // Respect the chosen operator (equals = matched, not-equals = not).
            return compareOp(
                cfg.operator,
                held ? 1 : 0,
                Number(cfg.value ?? 1),
                Number(cfg.value2 ?? 0)
            )
        }
        if (cfg && cfg.source === 'SCREEN_BUTTON') {
            const bid = cfg.buttonId || 'button1'
            const count = pressCountRef.current[bid] || 0
            const key = `${nodeId}:${bid}`
            const seen = consumedPressRef.current[key] || 0
            if (count > seen) {
                consumedPressRef.current[key] = count
                return true
            }
            return false
        }
        return evalCondition(cfg, perceptionRef.current || {})
    }

    // Watch live perception up to windowMs; true as soon as the condition holds.
    const waitForCondition = async (cfg, windowMs, nodeId) => {
        const end = performance.now() + windowMs
        while (runningRef.current) {
            if (conditionHolds(cfg, nodeId)) return true
            if (performance.now() >= end) return false
            await sleepUntil(60)
        }
        return false
    }

    // Build a nested program tree from the flowchart, mirroring how the
    // flowchart itself linearises: the sequential spine follows 'd' edges; an
    // 'if' branches via 'rYes' / 'rNo'; a 'loop' block carries its BODY on the
    // 'r' (right) handle and its continuation on 'd'. Result nodes:
    // { id, type, yes?, no?, sub? }.
    const buildProgram = (nodes, edges) => {
        const byId = {}
        nodes.forEach((n) => (byId[n.id] = n))
        const targetOf = (id, handle) => {
            let e = edges.find(
                (x) => x.source === id && x.sourceHandle === handle
            )
            if (!e && handle === 'd')
                e = edges.find(
                    (x) =>
                        x.source === id &&
                        (!x.sourceHandle || x.sourceHandle === 'd')
                )
            return e ? byId[e.target] : null
        }
        const anyTarget = (id) => {
            const e = edges.find((x) => x.source === id)
            return e ? byId[e.target] : null
        }
        const buildFrom = (node, depth) => {
            const list = []
            const seen = new Set()
            let cur = node
            while (cur && !seen.has(cur.id) && depth < 200 && list.length < 5000) {
                seen.add(cur.id)
                const type = cur.data.specificElType
                const item = { id: cur.id, type }
                if (type === 'if') {
                    item.yes = buildFrom(targetOf(cur.id, 'rYes'), depth + 1)
                    item.no = buildFrom(targetOf(cur.id, 'rNo'), depth + 1)
                } else if (type === 'loop' || type === 'end/repeat') {
                    item.sub = buildFrom(targetOf(cur.id, 'r'), depth + 1)
                }
                list.push(item)
                if (type === 'end' || type === 'end1') break
                cur = targetOf(cur.id, 'd')
            }
            return list
        }
        const start = nodes.find((n) => n.data.specificElType === 'start')
        const first = start ? anyTarget(start.id) : nodes[0]
        return buildFrom(first, 0)
    }

    // Does the program contain any loop/repeat block (at any depth)? When it
    // does, decisions are checked instantly and the loop provides repetition;
    // otherwise a one-shot run gives each decision a short watch window.
    const programHasLoop = (list) =>
        (list || []).some(
            (n) =>
                n.type === 'loop' ||
                n.type === 'end/repeat' ||
                programHasLoop(n.yes) ||
                programHasLoop(n.no) ||
                programHasLoop(n.sub)
        )

    // Interpret a program list in order. Returns 'end' | 'stop' | 'done' so an
    // End block (or Stop press) inside a branch/loop unwinds the whole program.
    const execList = async (list, all, ifWindow) => {
        for (const node of list) {
            if (!runningRef.current) return 'stop'
            const t = node.type
            if (t === 'end' || t === 'end1') return 'end'
            if (t === 'wait') {
                await sleepUntil(waitBlockMs(node.id))
                continue
            }
            if (t === 'output') {
                applyOutputActions(node.id, all)
                await yieldFrame()
                continue
            }
            if (t === 'if') {
                const ok = await waitForCondition(all[node.id], ifWindow, node.id)
                const branch = ok ? node.yes : node.no
                if (branch && branch.length) {
                    const r = await execList(branch, all, ifWindow)
                    // An End inside a branch just ends THAT branch — it must not
                    // stop the whole program (so a terminal Loop keeps running).
                    // Only a user Stop propagates.
                    if (r === 'stop') return r
                }
                continue
            }
            if (t === 'loop' || t === 'end/repeat') {
                const r = await runLoop(node, all, ifWindow)
                if (r === 'stop') return r
                continue
            }
            // start / unknown → nothing to run.
        }
        return 'done'
    }

    // Repeat `body` (a loop block's own body, or the whole program for a
    // terminal loop) `count` times from sessionStorage `loop{id}` — empty/0 =
    // forever. Paces to ~detection rate when the body has no wait of its own.
    const runLoop = async (node, all, ifWindow, bodyOverride) => {
        const body = bodyOverride || node.sub || []
        const cnt = parseInt(sessionStorage.getItem(`loop${node.id}`), 10)
        const infinite = !Number.isFinite(cnt) || cnt <= 0
        let k = 0
        while (runningRef.current && (infinite || k < cnt)) {
            if (body.length) {
                const r = await execList(body, all, ifWindow)
                // An End at the tail of the loop body ends that iteration, not
                // the loop — so the loop still runs its full count. Only a user
                // Stop breaks out.
                if (r === 'stop') return r
            } else {
                await sleepUntil(50)
            }
            k += 1
            await yieldFrame()
        }
        return 'done'
    }

    const runProgram = async () => {
        const elements = readJSON('flowchart-elements', [])
        const nodes = elements.filter((e) => e && e.data)
        const edges = elements.filter((e) => e && e.source && e.target)
        const all = readJSON('allDetails', {})
        const program = buildProgram(nodes, edges)
        console.log('[aiCode] parsed program:', JSON.stringify(program))
        firedOutputsRef.current = {}
        if (!program.length) {
            programStatusRef.current = 'empty program'
            return
        }
        const hasLoop = programHasLoop(program)
        const ifWindow = hasLoop ? 150 : 1500
        programStatusRef.current = 'running'

        // A Loop/repeat block placed at the end of the spine with no body of
        // its own means "repeat the whole program"; otherwise run the program
        // (container loops repeat their own body inside execList).
        const last = program[program.length - 1]
        const terminalLoop =
            last &&
            (last.type === 'loop' || last.type === 'end/repeat') &&
            (!last.sub || !last.sub.length)
        if (terminalLoop) {
            await runLoop(last, all, ifWindow, program.slice(0, -1))
        } else {
            await execList(program, all, ifWindow)
        }
        programStatusRef.current = 'ended'
        if (runningRef.current) stop()
    }

    // One detection tick: refresh perception + overlays only. Decisions and
    // actions are driven separately by the sequential program walker so the
    // flowchart's start/if/output/wait/loop/end order is honoured.
    const tick = async () => {
        if (!runningRef.current) return
        // Skip if the previous tick is still running so async inference doesn't
        // stack up (which would flood the main thread and hang the page).
        if (tickBusyRef.current) return
        tickBusyRef.current = true
        try {
            await detectTick()
        } finally {
            tickBusyRef.current = false
        }
    }

    const detectTick = async () => {
        const perception = await runDetection()
        if (!runningRef.current) return

        // Refresh any value/count-bound text so it tracks the counter live even
        // when its display block isn't re-run this tick by the walker.
        const bindings = liveTextBindingsRef.current
        const boundIds = Object.keys(bindings)
        if (boundIds.length) {
            const ctx = {
                count: partCountRef.current,
                vars: varsRef.current,
                transcript:
                    (liveTranscriptRef.current &&
                        liveTranscriptRef.current.text) ||
                    '',
            }
            let changed = false
            const outputs = { ...firedOutputsRef.current }
            boundIds.forEach((outId) => {
                const txt = resolveActionText(bindings[outId], ctx)
                if (!outputs[outId] || outputs[outId].text !== txt) {
                    outputs[outId] = { kind: 'text', text: txt }
                    changed = true
                }
            })
            if (changed) {
                firedOutputsRef.current = outputs
                setFiredOutputs(outputs)
            }
        }

        // Live overlay: draw the trail/line; surface the running crossing count.
        drawTrail()
        if (crossTotalRef.current !== crossCountRef.current) {
            crossCountRef.current = crossTotalRef.current
            const last = crossLogRef.current[0]
            setCrossInfo({
                count: crossTotalRef.current,
                dir: last ? last.dir : null,
            })
        }

        // Measured FPS (smoothed) + a detection/confidence log for the overlay.
        const nowT = performance.now()
        if (lastTickTimeRef.current) {
            const inst = 1000 / Math.max(1, nowT - lastTickTimeRef.current)
            fpsEmaRef.current = fpsEmaRef.current
                ? fpsEmaRef.current * 0.8 + inst * 0.2
                : inst
        }
        lastTickTimeRef.current = nowT

        const lines = [{ label: 'FPS', value: Math.round(fpsEmaRef.current) }]
        const rec = perception.recognition
        if (rec) {
            lines.push({
                label: 'Recognition',
                value: `${rec.className ?? '—'} ${Math.round(
                    (rec.conf || 0) * 100
                )}%`,
            })
        }
        if (perception.gesture) {
            lines.push({
                label: 'Gesture',
                value: `${perception.gesture.name} ${Math.round(
                    (perception.gesture.conf || 0) * 100
                )}%`,
            })
        }
        lines.push({
            label: 'Motion',
            value: perception.motion.moving
                ? `YES (${Math.round(perception.motion.level || 0)}%)`
                : 'NO',
        })
        if (perception.face.present) {
            lines.push({
                label: 'Face x,y',
                value: `${perception.face.x}, ${perception.face.y}`,
            })
        }
        // Tracked coordinates of the largest detected object.
        if (perception.objects && perception.objects.length) {
            const top = perception.objects.reduce(
                (best, o) => (!best || o.size > best.size ? o : best),
                null
            )
            if (top)
                lines.push({
                    label: `${top.class} x,y`,
                    value: `${top.cx}, ${top.cy}`,
                })
        }
        if (perception.spokenWord && Date.now() - perception.spokenWord.at < 2500)
            lines.push({ label: 'Heard', value: perception.spokenWord.text })
        if (perception.speechTranscript)
            lines.push({
                label: 'Transcript',
                value: perception.speechTranscript.slice(-60),
            })
        lines.push({ label: 'Passed', value: crossTotalRef.current })
        if (countPartsEnabledRef.current)
            lines.push({ label: 'Parts', value: partCountRef.current })
        if (ocrEnabledRef.current)
            lines.push({
                label: 'OCR',
                value:
                    ocrTextRef.current && ocrTextRef.current.text
                        ? ocrTextRef.current.text.replace(/\s+/g, ' ').slice(0, 40)
                        : '…',
            })
        if (markerEnabledRef.current)
            lines.push({
                label: 'Markers',
                value:
                    markersRef.current && markersRef.current.ids.length
                        ? markersRef.current.ids.join(', ')
                        : 'none',
            })
        // Live intensity of the selected colour(s) — how closely the scene
        // matches each rule's target, as a 0-100% reading with a match tick.
        if (colorEnabledRef.current)
            colorReadoutsRef.current.forEach((c) =>
                lines.push({
                    label: c.name,
                    value: `${c.intensity}%${c.matched ? ' ✓' : ''}`,
                })
            )
        // Live counter/flag values (flags shown as On/Off).
        loadEnabledVars().forEach((v) => {
            if (!(v.id in varsRef.current)) return
            const raw = varsRef.current[v.id]
            lines.push({
                label: v.name,
                value: v.kind === 'flag' ? (raw ? 'On' : 'Off') : raw,
            })
        })
        // Recent path-completion events (target + outcome).
        crossLogRef.current.slice(0, 3).forEach((c) => {
            lines.push({ label: `↳ ${c.label}`, value: pathDirLabel(c.dir) })
        })
        const keys = Array.from(perception.keys || [])
        if (keys.length) lines.push({ label: 'Keys', value: keys.join(', ') })
        const mouseBtns = Array.from(perception.mouse || [])
        if (mouseBtns.length)
            lines.push({ label: 'Mouse', value: mouseBtns.join(', ') })
        if (programStatusRef.current)
            lines.push({ label: 'Program', value: programStatusRef.current })
        setStats(lines)
    }

    // All configured hardware actions across the program.
    const collectHardwareActions = () => {
        const all = readJSON('allDetails', {})
        // Only nodes that still exist in the current flowchart — otherwise a
        // stale hardware action from a deleted node would wrongly demand a
        // device connection.
        const elements = readJSON('flowchart-elements', [])
        const liveIds = new Set(
            elements.filter((e) => e && e.data).map((e) => e.id)
        )
        const list = []
        Object.entries(all).forEach(([id, cfg]) => {
            if (!liveIds.has(id)) return
                ; (cfg?.actions || []).forEach((a) => {
                    if (
                        a.kind === 'hardware' &&
                        (a.accessory === '__servo__'
                            ? a.servoId != null
                            : a.accessory && a.port)
                    )
                        list.push(a)
                })
        })
        return list
    }

    // Live Setup ('LS') packet — declares which port carries which accessory.
    // Must be sent once before streaming 'LW' value packets, or the device
    // ignores the writes.
    const sendLiveSetup = (hardwareActions) => {
        if (!props.worker || !hardwareActions.length) return
        const connectedDevice =
            sessionStorage.getItem('connectedDevice') || 'Ace'
        const portMap = livePortsNumber[connectedDevice] || livePortsNumber.Ace
        // Small devices size the LS packet to their port count; Ace uses 20.
        const len = livePortsNumber[connectedDevice]
            ? Object.keys(livePortsNumber[connectedDevice]).length + 2
            : 20
        const data = new Array(len || 20).fill('O'.charCodeAt(0))
        data[0] = 'L'.charCodeAt(0)
        data[1] = 'S'.charCodeAt(0)
        hardwareActions.forEach((a) => {
            const c = compName_character[a.accessory]
            if (c == null) return
            if (MOTOR_ACCS.includes(a.accessory)) {
                // A motor needs BOTH bridge pins declared, or the reverse pin
                // (backward direction) is ignored by the board (bug 53).
                motorPinPorts(connectedDevice, a.port).forEach((p) => {
                    data[p + 1] = c
                })
            } else {
                const p = portMap?.[a.port]
                if (p != null) data[p + 1] = c
            }
        })
        props.worker.postMessage({ type: 'writeArray', value: data })
        console.log('[aiCode] live setup (LS) sent:', data)
    }

    // Open (or switch) the webcam. Stops any current stream first so switching
    // devices doesn't leave the old camera on.
    const openCamera = async (deviceId) => {
        try {
            const isMobile = deviceId === MOBILE_CAMERA_ID
            // Phone chosen but NOT connected: do NOT auto-fall back to a local
            // camera. Leave the feed as-is and just show a minimized warning —
            // the user stays in control (reconnect, or pick a camera). No popup.
            if (isMobile && !mobileConnected()) {
                activeCameraIdRef.current = MOBILE_CAMERA_ID
                setActiveCameraId(MOBILE_CAMERA_ID)
                sessionStorage.setItem('aiCameraDeviceId', MOBILE_CAMERA_ID)
                setPhoneStatus('dropped')
                if (streamRef.current) {
                    stopCameraStream(streamRef.current)
                    streamRef.current = null
                    if (videoRef.current) videoRef.current.srcObject = null
                }
                return
            }
            // Acquire first; only stop the old stream on success so a failed
            // switch never leaves a dead feed.
            const stream = await acquireCameraStream(deviceId)
            const prev = streamRef.current
            streamRef.current = stream
            if (prev && prev !== stream) stopCameraStream(prev)
            if (videoRef.current) {
                videoRef.current.srcObject = stream
                await videoRef.current.play().catch(() => { })
            }
            if (isMobile) {
                // The real phone stream arrived.
                activeCameraIdRef.current = MOBILE_CAMERA_ID
                setActiveCameraId(MOBILE_CAMERA_ID)
                sessionStorage.setItem('aiCameraDeviceId', MOBILE_CAMERA_ID)
                setPhoneStatus('connected')
            } else {
                const settings =
                    stream.getVideoTracks()[0]?.getSettings?.() || {}
                activeCameraIdRef.current = settings.deviceId || deviceId || ''
                setActiveCameraId(activeCameraIdRef.current)
                setPhoneStatus(null)
                // Keep the shared camera choice in sync across Configure/Execute.
                if (activeCameraIdRef.current)
                    sessionStorage.setItem(
                        'aiCameraDeviceId',
                        activeCameraIdRef.current
                    )
            }
        } catch (e) {
            // A stale/invalid stored deviceId (used as an `exact` constraint)
            // fails after the device list changed or site data was cleared, which
            // killed the feed on Run (bug 43). Retry with the default camera.
            if (deviceId && deviceId !== MOBILE_CAMERA_ID) {
                try {
                    const stream = await acquireCameraStream('')
                    const prev = streamRef.current
                    streamRef.current = stream
                    if (prev && prev !== stream) stopCameraStream(prev)
                    if (videoRef.current) {
                        videoRef.current.srcObject = stream
                        await videoRef.current.play().catch(() => { })
                    }
                    const settings =
                        stream.getVideoTracks()[0]?.getSettings?.() || {}
                    activeCameraIdRef.current = settings.deviceId || ''
                    setActiveCameraId(activeCameraIdRef.current)
                    if (activeCameraIdRef.current)
                        sessionStorage.setItem(
                            'aiCameraDeviceId',
                            activeCameraIdRef.current
                        )
                } catch (e2) {
                    /* no camera available */
                }
            }
        }
    }

    const refreshCameras = async () => {
        try {
            const opts = await listCameraOptions()
            // Execute has no QR to pair a phone, so only offer the Phone Camera
            // when it's already connected (or currently the active source).
            // Otherwise switching to it would silently fail (bug 41).
            const filtered = opts.filter(
                (o) =>
                    o.deviceId !== MOBILE_CAMERA_ID ||
                    mobileConnected() ||
                    activeCameraIdRef.current === MOBILE_CAMERA_ID
            )
            setCameras(filtered)
        } catch (e) {
            /* ignore */
        }
    }

    // Switch camera live without stopping the run loop.
    const selectCamera = async (deviceId) => {
        setCamMenuOpen(false)
        if (deviceId === activeCameraIdRef.current) return
        activeCameraIdRef.current = deviceId
        faceTrackPrevRef.current = null
        if (motionRef.current) motionRef.current.reset()
        await openCamera(deviceId)
    }

    // Spoken-keyword detection via the browser Web Speech API. Keeps the latest
    // transcript in spokenWordRef; the SPEECH_WORD source matches against it.
    const startSpeechRecognition = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition
        if (!SR || speechRecogRef.current) return
        try {
            const rec = new SR()
            rec.continuous = true
            rec.interimResults = true
            rec.lang = 'en-US'
            rec.onresult = (e) => {
                let seg = ''
                let finals = ''
                let interim = ''
                for (let i = e.resultIndex; i < e.results.length; i++) {
                    const r = e.results[i]
                    const t = r[0].transcript
                    seg += t
                    if (r.isFinal) finals += t
                    else interim += t
                }
                // Recent segment for the SPEECH_WORD keyword match.
                spokenWordRef.current = {
                    text: seg.toLowerCase(),
                    at: Date.now(),
                }
                // Accumulate finalised speech into the running transcript.
                if (finals.trim())
                    transcriptRef.current = (
                        transcriptRef.current +
                        ' ' +
                        finals.trim()
                    ).trim()
                liveTranscriptRef.current = {
                    text: (transcriptRef.current + ' ' + interim).trim(),
                    at: Date.now(),
                }
            }
            // Chrome ends recognition periodically; restart while running.
            rec.onend = () => {
                if (runningRef.current && speechRecogRef.current === rec) {
                    try {
                        rec.start()
                    } catch (_) { }
                }
            }
            rec.onerror = () => { }
            rec.start()
            speechRecogRef.current = rec
        } catch (e) {
            console.warn('[aiCode] speech recognition failed', e)
        }
    }

    const stopSpeechRecognition = () => {
        const rec = speechRecogRef.current
        speechRecogRef.current = null
        if (rec) {
            try {
                rec.onend = null
                rec.stop()
            } catch (_) { }
        }
        spokenWordRef.current = null
        transcriptRef.current = ''
        liveTranscriptRef.current = null
    }

    const start = async () => {
        const hardwareActions = collectHardwareActions()
        // Resolve every audio/video action's IndexedDB blob to an object URL up
        // front, so playback/display during the run is synchronous. Revoke any
        // URLs from a previous run first.
        Object.values(mediaUrlsRef.current).forEach((u) => {
            try {
                URL.revokeObjectURL(u)
            } catch (e) {
                /* ignore */
            }
        })
        mediaUrlsRef.current = {}
        const mediaAll = readJSON('allDetails', {})
        const mediaKeys = new Set()
        Object.values(mediaAll).forEach((cfg) => {
            ; (cfg?.actions || []).forEach((a) => {
                if (a && a.mediaKey && !a.src) mediaKeys.add(a.mediaKey)
            })
        })
        await Promise.all(
            [...mediaKeys].map(async (k) => {
                const url = await getMediaURL(k).catch(() => null)
                if (url) mediaUrlsRef.current[k] = url
            })
        )
        // Enable live part counting only when a PART_COUNT rule is present, and
        // capture its target model/class (class optional).
        const allDetailsForCount = readJSON('allDetails', {})
        const partRule = Object.values(allDetailsForCount).find(
            (c) => c && c.source === 'PART_COUNT' && c.modelId
        )
        // Also count when any output action pulls the live object count (into a
        // counter or a text display), so it works without an Object Count rule.
        const countActionPresent = Object.values(allDetailsForCount).some(
            (c) =>
                Array.isArray(c?.actions) &&
                c.actions.some(
                    (a) => a.mode === 'assign' && a.assignRef === 'count'
                )
        )
        countPartsEnabledRef.current = !!partRule || countActionPresent
        countTargetRef.current = partRule
            ? {
                modelId: String(partRule.modelId),
                classId:
                    partRule.selectedClassId &&
                        partRule.selectedClassId !== 'none'
                        ? partRule.selectedClassId
                        : null,
            }
            : null
        partCountRef.current = 0
        partBoxesRef.current = []

        // Enable live OCR only when an OCR rule is present.
        ocrEnabledRef.current = Object.values(allDetailsForCount).some(
            (c) => c && c.source === 'OCR_TEXT'
        )
        ocrTextRef.current = null
        ocrAtRef.current = 0
        ocrBusyRef.current = false

        // Enable ArUco marker detection only when a marker rule is present.
        markerEnabledRef.current = Object.values(allDetailsForCount).some(
            (c) => c && c.source === 'MARKER_ID'
        )
        markersRef.current = null
        markerAtRef.current = 0
        markerBusyRef.current = false

        // Reset per-run colour state and collect each colour rule's target so
        // the stats can show that colour's live intensity.
        colorStreakRef.current = {}
        colorReadoutsRef.current = []
        colorRulesRef.current = Object.values(allDetailsForCount)
            .filter((c) => c && c.source === 'DETECT_COLOR' && c.colorTarget)
            .map((c) => ({
                name: c.colorTarget.name || 'Colour',
                target: c.colorTarget,
            }))
        colorEnabledRef.current = colorRulesRef.current.length > 0

        // Reset counters/flags enabled in the Start block to 0 for this run.
        const startVars = {}
        loadEnabledVars().forEach((v) => (startVars[v.id] = 0))
        varsRef.current = startVars
        // Reset per-run input/output edge state.
        pressCountRef.current = {}
        consumedPressRef.current = {}
        liveTextBindingsRef.current = {}

        // Require a connected device before streaming live bytes.
        if (hardwareActions.length && !props.webSerial?.isConnected) {
            setShowDeviceDialog(true)
            return
        }

        console.log('[aiCode] program elements:', readJSON('flowchart-elements', []))
        console.log('[aiCode] program config (allDetails):', readJSON('allDetails', {}))
        console.log('[aiCode] enable:', enable, '| layout:', layout, '| fps:', fps)

        // Collect the ROI rectangles referenced by the program's rules so the
        // region overlay can draw them.
        const allCfg = readJSON('allDetails', {})
        const cam = props.camModels || []
        const rects = []
        Object.values(allCfg).forEach((cfg) => {
            if (
                cfg &&
                cfg.modelId &&
                cfg.selectedRegionId &&
                cfg.selectedRegionId !== 'none' &&
                cfg.selectedRegionId !== 'all'
            ) {
                const m = cam.find(
                    (x) => String(x.modelId) === String(cfg.modelId)
                )
                const r = (m?.roiRegions || []).find(
                    (rr) => String(rr.id) === String(cfg.selectedRegionId)
                )
                if (r && !rects.includes(r)) rects.push(r)
            } else if (cfg && cfg.modelId && cfg.selectedRegionId === 'all') {
                const m = cam.find(
                    (x) => String(x.modelId) === String(cfg.modelId)
                )
                    ; (m?.roiRegions || []).forEach((r) => {
                        if (!rects.includes(r)) rects.push(r)
                    })
            }
        })
        regionsToDrawRef.current = rects

        if (cameraEnabled) {
            // Drop a stale/invalid stored camera id before opening so getUserMedia
            // never overconstrains and kills the integrated-camera feed (bug 43).
            const camId = await sanitizeCameraId(activeCameraIdRef.current)
            if (camId !== activeCameraIdRef.current) {
                activeCameraIdRef.current = camId
                setActiveCameraId(camId)
                if (camId) sessionStorage.setItem('aiCameraDeviceId', camId)
                else sessionStorage.removeItem('aiCameraDeviceId')
            }
            await openCamera(camId)
            refreshCameras()
            if (!samplerRef.current) samplerRef.current = new FrameSampler()
            if (!motionRef.current) motionRef.current = new MotionDetector()
            motionRef.current.reset()
            motionLastRef.current = 0
            objPrevRef.current = {}
            if (objTrackerRef.current) objTrackerRef.current.reset()
            objLocalizeAtRef.current = 0
            customObjectsRef.current = []
            faceTrackPrevRef.current = null
            trailRef.current = []
            prevFaceRef.current = null
            // Load the pre-drawn crossing paths and reset crossing state — only
            // when the Tracking feature is selected, so a line drawn for a
            // now-removed feature doesn't reappear.
            pathsRef.current = trackingSelectedRef.current ? loadPaths() : []
            pathStatesRef.current = {}
            // Kick off the intro draw-on animation for the crossing line.
            if (pathsRef.current.length) {
                lineDrawStartRef.current = performance.now()
            }
            pathEventsRef.current = []
            crossLogRef.current = []
            crossTotalRef.current = 0
            crossCountRef.current = 0
            setCrossInfo(null)
            // Drop cached heads so a freshly retrained model is picked up.
            modelsRef.current = {}
            setModelLoading(true)
            await loadModels()
            setModelLoading(false)
        } else {
            modelsRef.current = {}
            await loadModels()
        }

        // Enter live mode exactly like the hardware Live button: send the
        // stop/play guard bytes, wait for the board to settle, then declare the
        // used ports/accessories (LS) before any 'LW' value packets stream.
        if (hardwareActions.length && props.worker) {
            const connectedDevice = sessionStorage.getItem('connectedDevice')
            const deviceVersion = sessionStorage.getItem('deviceVersion')
            props.worker.postMessage({
                type: 'writeArray',
                value: stopByteCondition({ connectedDevice, deviceVersion }),
            })
            await new Promise((r) => setTimeout(r, 100))
            sendLiveSetup(hardwareActions)
            lastLiveRef.current = {}
            await new Promise((r) => setTimeout(r, 60))
            // Smart-servo robots (Humanoid/Hexapod) need a one-time STOP before
            // position packets stream.
            const hasSmartServo = hardwareActions.some(
                (a) =>
                    a.accessory === '__servo__' &&
                    servoById(
                        sessionStorage.getItem('connectedDevice') || 'Ace',
                        a.servoId
                    )?.proto === 'sm'
            )
            if (hasSmartServo) {
                props.worker.postMessage({
                    type: 'writeArray',
                    value: [83, 84, 79, 80], // 'S','T','O','P'
                })
                await new Promise((r) => setTimeout(r, 60))
            }
        }

        // Speech recognition runs for a spoken-keyword rule, a Text output that
        // prints the live transcript, or whenever the microphone input is
        // selected (live captions).
        const allSpeechCfg = readJSON('allDetails', {})
        const needsSpeechWord = Object.values(allSpeechCfg).some(
            (cfg) => cfg && cfg.source === 'SPEECH_WORD'
        )
        const speechTextBound = Object.values(allSpeechCfg).some(
            (cfg) =>
                Array.isArray(cfg?.actions) &&
                cfg.actions.some(
                    (a) => a.mode === 'assign' && a.assignRef === 'speech'
                )
        )
        const micSelected = selectedFeatures.includes('inputs/microphone')
        transcriptRef.current = ''
        liveTranscriptRef.current = null
        runningRef.current = true
        if (needsSpeechWord || speechTextBound || micSelected)
            startSpeechRecognition()
        setRunning(true)
        const interval = Math.max(1000 / fps, 33)
        loopRef.current = setInterval(tick, interval)
            // Prime perception with one detection pass BEFORE the walker evaluates
            // its first decision, so an input that's already in front of the camera
            // when Run starts is detected right away (bug 38) — otherwise the first
            // decision sees an empty perception and needs the input to change first.
            ; (async () => {
                try {
                    await detectTick()
                } catch (e) {
                    /* ignore */
                }
                if (runningRef.current) runProgram()
            })()
    }

    const stop = () => {
        runningRef.current = false
        setRunning(false)
        if (loopRef.current) clearInterval(loopRef.current)
        loopRef.current = null
        stopCameraStream(streamRef.current) // leaves a paired phone stream live
        streamRef.current = null
        setFiredOutputs({})
        faceBoxRef.current = null
        objectBoxesRef.current = []
        customObjectsRef.current = []
        setModelLoading(false)
        setCrossInfo(null)
        setStats([])
        fpsEmaRef.current = 0
        lastTickTimeRef.current = 0
        trailRef.current = []
        prevFaceRef.current = null
        screenButtonsRef.current = {}
        const tcanvas = trailCanvasRef.current
        if (tcanvas) {
            const c = tcanvas.getContext('2d')
            c && c.clearRect(0, 0, tcanvas.width, tcanvas.height)
        }
        tickBusyRef.current = false
        firedOutputsRef.current = {}
        programStatusRef.current = ''
        // Free media object URLs resolved for this run.
        Object.values(mediaUrlsRef.current).forEach((u) => {
            try {
                URL.revokeObjectURL(u)
            } catch (e) {
                /* ignore */
            }
        })
        mediaUrlsRef.current = {}
        stopSpeechRecognition()
        // Turn off every configured port-based output (value 0) in ONE batched
        // live-write frame. Robot smart servos are left as-is (no neutral).
        const connectedDevice =
            sessionStorage.getItem('connectedDevice') || 'Ace'
        collectHardwareActions().forEach((a) => {
            if (a.accessory === '__servo__') return
            const char = compName_character[a.accessory]
            if (char == null) return
            if (MOTOR_ACCS.includes(a.accessory)) {
                motorPinPorts(connectedDevice, a.port).forEach((p) => {
                    liveStateRef.current[p] = { char, value: 0 }
                })
            } else {
                const portNum =
                    livePortsNumber?.[connectedDevice]?.[a.port] ??
                    livePortsNumber.Ace?.[a.port]
                if (portNum != null)
                    liveStateRef.current[portNum] = { char, value: 0 }
            }
        })
        flushLiveWrite()
        liveStateRef.current = {}
        lastLiveRef.current = {}
    }

    // Free all GPU/model resources. The shared MediaPipe gesture recognizer is a
    // singleton reused by play mode, so it is detached (not closed) here.
    const disposeModels = () => {
        Object.values(modelsRef.current).forEach((m) => {
            try {
                m.head.dispose()
            } catch (_) { }
        })
        modelsRef.current = {}
        try {
            mobilenetRef.current?.dispose()
        } catch (_) { }
        mobilenetRef.current = null
        // MediaPipe pose landmarker is memoized in mediapipeRuntime and reused
        // across runs; just drop our reference (don't close the shared task).
        poseModelRef.current = null
        gestureRecognizerRef.current = null
        cocoModelRef.current = null
        objTrackerRef.current = null
        faceDetectorRef.current = null
    }

    useEffect(() => {
        return () => {
            runningRef.current = false
            if (loopRef.current) clearInterval(loopRef.current)
            if (streamRef.current)
                streamRef.current.getTracks().forEach((t) => t.stop())
            stopSpeechRecognition()
            disposeModels()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Track keyboard and mouse state globally so input-trigger rules can read
    // it. Presses on the on-screen buttons (data-screen-btn) are excluded so
    // they don't also register as a generic mouse click.
    useEffect(() => {
        const onKeyDown = (e) => keysDownRef.current.add(normalizeKey(e.key))
        const onKeyUp = (e) => keysDownRef.current.delete(normalizeKey(e.key))
        const onMouseDown = (e) => {
            if (e.target?.dataset?.screenBtn) return
            mouseButtonsRef.current.add(mouseButtonName(e.button))
        }
        const onMouseUp = (e) =>
            mouseButtonsRef.current.delete(mouseButtonName(e.button))
        const onBlur = () => {
            keysDownRef.current.clear()
            mouseButtonsRef.current.clear()
            screenButtonsRef.current = {}
        }
        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('keyup', onKeyUp)
        window.addEventListener('mousedown', onMouseDown)
        window.addEventListener('mouseup', onMouseUp)
        window.addEventListener('blur', onBlur)
        return () => {
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('keyup', onKeyUp)
            window.removeEventListener('mousedown', onMouseDown)
            window.removeEventListener('mouseup', onMouseUp)
            window.removeEventListener('blur', onBlur)
        }
    }, [])

    const goBack = () => {
        stop()
        sessionStorage.setItem('slideDirection', true)
        history.push('/aiCodeFlow')
    }

    // Full-screen the play area (just the screen, no page header). Uses the
    // native Fullscreen API so Esc exits; state mirrors it for the button label.
    const stageRef = useRef(null)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const toggleFullscreen = () => {
        const el = stageRef.current
        if (!el) return
        if (document.fullscreenElement) {
            document.exitFullscreen && document.exitFullscreen()
        } else if (el.requestFullscreen) {
            el.requestFullscreen().catch(() => { })
        }
    }
    useEffect(() => {
        const onFsChange = () =>
            setIsFullscreen(!!document.fullscreenElement)
        document.addEventListener('fullscreenchange', onFsChange)
        return () =>
            document.removeEventListener('fullscreenchange', onFsChange)
    }, [])

    return (
        <div className={styles.container}>
            <DialogModal
                show={showDeviceDialog}
                text={
                    <p>
                        Please connect your device to run this program. Use the
                        device connectivity option to connect, then press play
                        again.
                    </p>
                }
                showCloseBtn={true}
                handleDialogModalClose={() => setShowDeviceDialog(false)}
                optionsToSelect={[
                    { text: 'OK', do: () => setShowDeviceDialog(false) },
                ]}
            />

            <div className={styles.header}>
                <ProgramHeader data={headerData} showSave={false} showHelp={true} />
            </div>

            <div
                className={styles.stage}
                ref={stageRef}
                style={
                    isFullscreen
                        ? {
                            width: '100vw',
                            height: '100vh',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#000',
                        }
                        : null
                }
            >
                {/* The screen mirrors the current device's aspect ratio so the
                    layout matches what shows on the device. */}
                <div className={styles.screen}>
                    {modelLoading && (
                        <div className={styles.loadingOverlay}>
                            <div className={styles.spinner} />
                            <p>Loading AI model…</p>
                        </div>
                    )}

                    <button
                        type="button"
                        className={styles.statsToggle}
                        onClick={() => setShowStats((v) => !v)}
                        title={showStats ? 'Hide stats' : 'Show stats'}
                    >
                        {showStats ? 'Hide Stats' : 'Show Stats'}
                    </button>
                    <button
                        type="button"
                        className={styles.fullscreenToggle}
                        onClick={toggleFullscreen}
                        title={
                            isFullscreen ? 'Exit full screen' : 'Full screen'
                        }
                    >
                        {isFullscreen ? <Shrink size="15" /> : <Expand size="15" />}
                    </button>
                    {showStats && (
                        <div className={styles.statsPanel}>
                            <div className={styles.statsHead}>Detection Log</div>
                            {/* Overlay switches — compact, in-theme. */}
                            {trackingSelected && (
                                <>
                                    <div className={styles.statsRow}>
                                        <span className={styles.statsLabel}>
                                            Trail
                                        </span>
                                        <ToggleSwitch
                                            size="sm"
                                            tone="gray"
                                            checked={showTrail}
                                            onChange={toggleTrail}
                                            ariaLabel="Toggle trail"
                                        />
                                    </div>
                                    <div className={styles.statsRow}>
                                        <span className={styles.statsLabel}>
                                            Trajectory
                                        </span>
                                        <ToggleSwitch
                                            size="sm"
                                            tone="gray"
                                            checked={showTrajectory}
                                            onChange={toggleTrajectory}
                                            ariaLabel="Toggle trajectory"
                                        />
                                    </div>
                                </>
                            )}
                            <div className={styles.statsRow}>
                                <span className={styles.statsLabel}>Region</span>
                                <ToggleSwitch
                                    size="sm"
                                    tone="gray"
                                    checked={showRegion}
                                    onChange={toggleRegion}
                                    ariaLabel="Toggle region"
                                />
                            </div>
                            {trackingSelected && (
                                <div className={styles.statsRow}>
                                    <span className={styles.statsLabel}>
                                        Crossing line
                                    </span>
                                    <ToggleSwitch
                                        size="sm"
                                        tone="gray"
                                        checked={showLine}
                                        onChange={toggleLine}
                                        ariaLabel="Toggle crossing line"
                                    />
                                </div>
                            )}
                            <div className={styles.statsRow}>
                                <span className={styles.statsLabel}>
                                    Detection boxes
                                </span>
                                <ToggleSwitch
                                    size="sm"
                                    tone="gray"
                                    checked={showBoxes}
                                    onChange={toggleBoxes}
                                    ariaLabel="Toggle detection boxes"
                                />
                            </div>
                            {countPartsEnabledRef.current && (
                                <div className={styles.statsRow}>
                                    <span className={styles.statsLabel}>
                                        Count markers
                                    </span>
                                    <ToggleSwitch
                                        size="sm"
                                        tone="gray"
                                        checked={showCount}
                                        onChange={toggleCount}
                                        ariaLabel="Toggle count markers"
                                    />
                                </div>
                            )}
                            {stats.length === 0 ? (
                                <div className={styles.statsRow}>
                                    <span>Press play to start…</span>
                                </div>
                            ) : (
                                stats.map((s, i) => (
                                    <div key={i} className={styles.statsRow}>
                                        <span className={styles.statsLabel}>
                                            {s.label}
                                        </span>
                                        <span className={styles.statsValue}>
                                            {String(s.value)}
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                    {cameraEnabled && (
                        <div
                            ref={cameraWidgetRef}
                            className={styles.widget}
                            style={{
                                ...posStyle(layout.camera, {
                                    x: 8,
                                    y: 8,
                                    w: 84,
                                    h: 55,
                                }),
                                ...(cameraHidden
                                    ? {
                                        opacity: 0,
                                        pointerEvents: 'none',
                                        zIndex: 0,
                                    }
                                    : null),
                            }}
                        >
                            <video
                                ref={videoRef}
                                className={styles.video}
                                autoPlay
                                playsInline
                                muted
                            />
                            {lineEditMode && (
                                <div
                                    className={styles.lineEditLayer}
                                    onPointerDown={onEditDown}
                                    onPointerMove={onEditMove}
                                    onPointerUp={onEditUp}
                                    onPointerLeave={onEditLeave}
                                />
                            )}
                            {/* <div className={styles.lineTools}>
                                <button
                                    type="button"
                                    className={`${styles.lineToolBtn} ${lineEditMode ? styles.lineToolBtnOn : ''
                                        }`}
                                    onClick={() => setLineEditMode((v) => !v)}
                                >
                                    {lineEditMode ? 'Done' : 'Edit line'}
                                </button>
                                {lineEditMode && (
                                    <>
                                        <button
                                            type="button"
                                            className={styles.lineToolBtn}
                                            onClick={undoEditPoint}
                                        >
                                            Undo
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.lineToolBtn}
                                            onClick={clearEditPath}
                                        >
                                            Clear
                                        </button>
                                    </>
                                )}
                            </div>
                            {lineEditMode && (
                                <div className={styles.lineHint}>
                                    Draw the route in order: move the cursor to
                                    preview, click to drop a point, drag to
                                    adjust.
                                </div>
                            )} */}
                            {showTrajectory && (
                                <div className={styles.trajBar}>
                                    <span className={styles.trajBarLabel}>
                                        Track
                                    </span>
                                    <select
                                        className={styles.trackSelect}
                                        value={selectedObjId}
                                        onChange={(e) =>
                                            selectObj(e.target.value)
                                        }
                                    >
                                        <option value="">
                                            Auto (largest)
                                        </option>
                                        {detectedIds.map((o) => (
                                            <option key={o.id} value={o.id}>
                                                #{o.id} {o.class}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            {/* Face + object boxes are drawn on the overlay
                                canvas (contain transform) so they align with the
                                letterboxed video. */}
                            <canvas
                                ref={trailCanvasRef}
                                className={styles.trailCanvas}
                            />
                            {/*{crossInfo && (
                                <div className={styles.crossBadge}>
                                    Passed: {crossInfo.count}
                                    {crossInfo.dir
                                        ? ` (${pathDirLabel(crossInfo.dir)})`
                                        : ''}
                                </div>
                            )}*/}

                            {/* Live camera device chooser */}
                            <div className={styles.camWrap}>
                                <button
                                    type="button"
                                    className={styles.camBtn}
                                    onClick={() => {
                                        refreshCameras()
                                        setCamMenuOpen((v) => !v)
                                    }}
                                    title="Switch camera"
                                >
                                    <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                    >
                                        <path
                                            d="M4 7.5A1.5 1.5 0 015.5 6h2.2l1-1.6A1 1 0 019.6 4h4.8a1 1 0 01.9.4l1 1.6h2.2A1.5 1.5 0 0120 7.5v9A1.5 1.5 0 0118.5 18h-13A1.5 1.5 0 014 16.5v-9z"
                                            stroke="currentColor"
                                            strokeWidth="1.7"
                                            strokeLinejoin="round"
                                        />
                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="3.2"
                                            stroke="currentColor"
                                            strokeWidth="1.7"
                                        />
                                    </svg>
                                </button>
                                {camMenuOpen && (
                                    <ul className={styles.camMenu}>
                                        {cameras.length === 0 && (
                                            <li className={styles.camEmpty}>
                                                No cameras found
                                            </li>
                                        )}
                                        {cameras.map((d) => (
                                            <li
                                                key={d.deviceId}
                                                className={`${styles.camItem} ${d.deviceId === activeCameraId
                                                    ? styles.camItemActive
                                                    : ''
                                                    }`}
                                                onClick={() =>
                                                    selectCamera(d.deviceId)
                                                }
                                                style={
                                                    d.deviceId ===
                                                        MOBILE_CAMERA_ID
                                                        ? {
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            justifyContent:
                                                                'space-between',
                                                            gap: '8px',
                                                        }
                                                        : undefined
                                                }
                                            >
                                                <span>{d.label}</span>
                                                {d.deviceId ===
                                                    MOBILE_CAMERA_ID && (
                                                        <span
                                                            role="button"
                                                            title="Reconnect / show QR"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setCamMenuOpen(false)
                                                                openPhoneHelp()
                                                            }}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Info
                                                                width={16}
                                                                height={16}
                                                            />
                                                        </span>
                                                    )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {phoneStatus === 'dropped' &&
                                !camMenuOpen &&
                                (activeCameraId === MOBILE_CAMERA_ID ||
                                    configuredCameraId === MOBILE_CAMERA_ID) && (
                                    <div
                                        onClick={openPhoneHelp}
                                        style={{
                                            position: 'absolute',
                                            top: 54,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            background: 'rgba(228,98,109,0.95)',
                                            color: '#fff',
                                            padding: '8px 14px',
                                            borderRadius: 10,
                                            fontSize: 13,
                                            fontFamily: 'Halcyon_Medium',
                                            cursor: 'pointer',
                                            zIndex: 8,
                                            boxShadow:
                                                '0 4px 10px rgba(0,0,0,0.25)',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Phone camera not connected — tap for
                                        options
                                    </div>
                                )}
                            {showPhoneHelp && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'rgba(0,0,0,0.7)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: 40,
                                    }}
                                    onClick={() => setShowPhoneHelp(false)}
                                >
                                    <div
                                        style={{
                                            background: '#fff',
                                            color: '#1b2430',
                                            borderRadius: 16,
                                            padding: 22,
                                            textAlign: 'center',
                                            maxWidth: 300,
                                            position: 'relative',
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            type="button"
                                            onClick={closePhoneHelp}
                                            style={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                border: 'none',
                                                background: 'transparent',
                                                cursor: 'pointer',
                                                color: '#1b2430',
                                            }}
                                            aria-label="Close"
                                        >
                                            <X width={18} height={18} />
                                        </button>
                                        <p
                                            style={{
                                                margin: '0 0 8px',
                                                fontWeight: 600,
                                                fontSize: 16,
                                            }}
                                        >
                                            Phone camera not connected
                                        </p>
                                        {/* Reconnect via the SAME session — no QR
                                            needed if the phone's camera page is
                                            still open. */}
                                        <ol
                                            style={{
                                                textAlign: 'left',
                                                fontSize: 13,
                                                lineHeight: 1.5,
                                                margin: '0 0 12px',
                                                paddingLeft: 18,
                                            }}
                                        >
                                            <li>
                                                On your phone, open{' '}
                                                <strong>Chrome</strong>.
                                            </li>
                                            <li>
                                                If the camera page is still open,
                                                tap{' '}
                                                <strong>Share camera</strong>{' '}
                                                again — it reconnects to the same
                                                session (no QR needed).
                                            </li>
                                            <li>
                                                If that page is closed, use{' '}
                                                <strong>
                                                    Connect with QR code
                                                </strong>{' '}
                                                below.
                                            </li>
                                            <li>
                                                If the phone says{' '}
                                                <strong>
                                                    “code already in use”
                                                </strong>{' '}
                                                (common after it changed Wi-Fi),
                                                tap{' '}
                                                <strong>Get a fresh code</strong>{' '}
                                                and scan the new QR.
                                            </li>
                                        </ol>
                                        {phoneQr ? (
                                            <>
                                                <img
                                                    src={phoneQr}
                                                    alt="Scan to connect your phone"
                                                    style={{
                                                        width: 190,
                                                        height: 190,
                                                    }}
                                                />
                                                {phoneRoom && (
                                                    <p
                                                        style={{
                                                            margin: '6px 0 0',
                                                            fontSize: 13,
                                                        }}
                                                    >
                                                        Code{' '}
                                                        <strong>
                                                            {phoneRoom}
                                                        </strong>
                                                    </p>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={freshPhoneQr}
                                                    style={{
                                                        display: 'block',
                                                        margin: '8px auto 0',
                                                        border: 'none',
                                                        background: 'transparent',
                                                        color: '#2b6cff',
                                                        fontSize: 12,
                                                        textDecoration:
                                                            'underline',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    Get a fresh code
                                                </button>
                                                <ChromeNote />
                                            </>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={revealPhoneQr}
                                                style={{
                                                    width: '100%',
                                                    border: 'none',
                                                    background:
                                                        'linear-gradient(96deg,#ffb63f,#f4813f)',
                                                    color: '#fff',
                                                    borderRadius: 12,
                                                    padding: '10px 12px',
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Connect with QR code
                                            </button>
                                        )}
                                        {/* Or pick a different available camera. */}
                                        {(() => {
                                            const others = cameras.filter(
                                                (c) =>
                                                    c.deviceId &&
                                                    c.deviceId !==
                                                    MOBILE_CAMERA_ID
                                            )
                                            return (
                                                <div
                                                    style={{
                                                        marginTop: 14,
                                                        borderTop:
                                                            '1px solid #eee',
                                                        paddingTop: 12,
                                                    }}
                                                >
                                                    {others.length ? (
                                                        <button
                                                            type="button"
                                                            onClick={
                                                                pickAnotherCamera
                                                            }
                                                            style={{
                                                                width: '100%',
                                                                border: '1.5px solid #cfcfe0',
                                                                background: '#fff',
                                                                color: '#2b2963',
                                                                borderRadius: 12,
                                                                padding:
                                                                    '10px 12px',
                                                                fontSize: 13,
                                                                fontWeight: 600,
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            Connect to another
                                                            camera
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <p
                                                                style={{
                                                                    margin: '0 0 8px',
                                                                    fontSize: 13,
                                                                }}
                                                            >
                                                                No other camera
                                                                found.
                                                            </p>
                                                            <button
                                                                type="button"
                                                                onClick={
                                                                    stopFromPhoneHelp
                                                                }
                                                                style={{
                                                                    width: '100%',
                                                                    border: 'none',
                                                                    background:
                                                                        '#e4626d',
                                                                    color: '#fff',
                                                                    borderRadius: 12,
                                                                    padding:
                                                                        '10px 12px',
                                                                    fontSize: 13,
                                                                    fontWeight: 600,
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                Stop run
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )
                                        })()}
                                        {/* Terminate the phone-camera session so
                                            a completely fresh pairing can begin. */}
                                        <button
                                            type="button"
                                            onClick={disconnectPhone}
                                            style={{
                                                display: 'block',
                                                margin: '12px auto 0',
                                                border: 'none',
                                                background: 'transparent',
                                                color: '#8a8a99',
                                                fontSize: 12,
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            Disconnect phone camera
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    {/* Output instances placed in Configure > Output. Text/
                        Image/Video show their fired content; Button instances
                        are clickable on-screen buttons feeding the rules. */}
                    {outputInstances.map((o) => {
                        const pos = { x: o.x, y: o.y, w: o.w, h: o.h }
                        if (o.type === 'button') {
                            return (
                                <button
                                    key={o.id}
                                    type="button"
                                    data-screen-btn={o.id}
                                    className={styles.screenButton}
                                    style={posStyle(pos, pos)}
                                    onPointerDown={() => {
                                        screenButtonsRef.current[o.id] = true
                                        pressCountRef.current[o.id] =
                                            (pressCountRef.current[o.id] || 0) +
                                            1
                                    }}
                                    onPointerUp={() => {
                                        screenButtonsRef.current[o.id] = false
                                    }}
                                    onPointerLeave={() => {
                                        screenButtonsRef.current[o.id] = false
                                    }}
                                >
                                    {o.name}
                                </button>
                            )
                        }
                        if (o.type === 'toggle') {
                            const on = !!toggleStates[o.id]
                            return (
                                <button
                                    key={o.id}
                                    type="button"
                                    data-screen-btn={o.id}
                                    className={`${styles.screenToggle} ${on ? styles.screenToggleOn : ''
                                        }`}
                                    style={posStyle(pos, pos)}
                                    onPointerDown={(e) => {
                                        e.preventDefault()
                                        flipToggle(o.id)
                                    }}
                                >
                                    <span className={styles.screenToggleTrack}>
                                        <span
                                            className={styles.screenToggleKnob}
                                        />
                                    </span>
                                    <span className={styles.screenToggleLabel}>
                                        {o.name}
                                        {/* : {on ? 'ON' : 'OFF'} */}
                                    </span>
                                </button>
                            )
                        }
                        if (o.type === 'audio') {
                            // No visual when hidden; otherwise a small speaker
                            // marker (audio plays via the action either way).
                            if (o.hidden) return null
                            return (
                                <div
                                    key={o.id}
                                    className={styles.audioWidget}
                                    style={posStyle(pos, pos)}
                                >
                                    <Volume2 size={20} />
                                    <span>{o.name}</span>
                                </div>
                            )
                        }
                        const fired = firedOutputs[o.id]
                        if (o.type === 'text') {
                            if (!fired?.text) return null
                            return (
                                <div
                                    key={o.id}
                                    className={styles.textWidget}
                                    style={posStyle(pos, pos)}
                                >
                                    {fired.text}
                                </div>
                            )
                        }
                        // image / video
                        if (!fired?.src) return null
                        return (
                            <div
                                key={o.id}
                                className={styles.mediaWidget}
                                style={posStyle(pos, pos)}
                            >
                                {o.type === 'image' ? (
                                    <img
                                        className={styles.media}
                                        src={fired.src}
                                        alt={o.name}
                                    />
                                ) : (
                                    <video
                                        className={styles.media}
                                        src={fired.src}
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                    />
                                )}
                            </div>
                        )
                    })}
                    {!cameraEnabled &&
                        Object.keys(firedOutputs).length === 0 && (
                            <p className={styles.idle}>
                                {running
                                    ? 'Running…'
                                    : 'Press play to run your program'}
                            </p>
                        )}
                </div>
            </div>

            <div className={styles.footer}>
                <img
                    className={styles.backBtn}
                    src={renderImage('backBtn')}
                    alt="back"
                    onClick={goBack}
                />
                <img
                    className={styles.playBtn}
                    src={renderPrgImage(running ? 'stop' : 'play')}
                    alt={running ? 'stop' : 'play'}
                    onClick={running ? stop : start}
                />
            </div>
        </div>
    )
}

const mapStateToProps = (state) => ({
    worker: state.worker,
    webSerial: state.webSerial,
    appModeStorage: state.appModeStorage,
    camModels: state.appModeStorage?.trainMachine?.camModels || [],
    currentModelId: state.appModeStorage?.trainMachine?.currentModelId,
})
export default connect(mapStateToProps)(AiExecute)